import React, { useEffect, useMemo, useState } from "react";
import {
  RefreshCcw,
  Save,
  Settings2,
  Truck,
  Warehouse,
  Clock3,
  PackageCheck,
  ShieldCheck,
  X,
  CheckCircle2,
  AlertCircle,
  Coins,
  CalendarDays,
  CalendarRange,
  Plus,
  Trash2,
  Info,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { settingsService } from "../../../services/settings.service";
import { restaurantAdminService, rowsOf as restaurantRowsOf } from "../../../services/restaurantAdmin.service";
import NormalDeliverySettings from "./NormalDeliverySettings";
import PaymentDeliverySettings from "./PaymentDeliverySettings";

const BORDER = "#e2e8f0";
const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";

// Delivery settings are editable by the admin. Frontend validation below prevents
// internally inconsistent radius and driver-presence values from being saved.
const LOCK_CORE_DELIVERY_FIELDS = false;

function validateDeliverySettings(values = {}) {
  const numberValue = (key) => Number(values?.[key]);
  const integerValue = (key) => numberValue(key);

  const recentSeconds = numberValue("recentSeconds");
  const riderOnlineTtlSeconds = numberValue("riderOnlineTtlSeconds");
  const maxCandidates = integerValue("maxCandidates");
  const maxTotal = integerValue("defaultMaxActiveOrders");
  const maxFood = integerValue("maxFoodOrdersPerPartner");
  const maxGrocery = integerValue("maxGroceryOrdersPerPartner");

  const groceryInitial = numberValue("groceryInitialSearchKm");
  const groceryMax = numberValue("groceryMaxSearchKm");
  const foodInitial = numberValue("foodInitialSearchKm");
  const foodMax = numberValue("foodMaxSearchKm");

  if (!Number.isFinite(groceryInitial) || groceryInitial <= 0) {
    throw new Error("Grocery Initial Search Radius must be greater than 0 km.");
  }
  if (!Number.isFinite(groceryMax) || groceryMax < groceryInitial) {
    throw new Error("Grocery Maximum Search Radius must be greater than or equal to Grocery Initial Search Radius.");
  }
  if (!Number.isFinite(foodInitial) || foodInitial <= 0) {
    throw new Error("Food Initial Search Radius must be greater than 0 km.");
  }
  if (!Number.isFinite(foodMax) || foodMax < foodInitial) {
    throw new Error("Food Maximum Search Radius must be greater than or equal to Food Initial Search Radius.");
  }
  if (!Number.isFinite(recentSeconds) || recentSeconds <= 0) {
    throw new Error("Recent Seconds must be greater than 0.");
  }
  if (!Number.isFinite(riderOnlineTtlSeconds) || riderOnlineTtlSeconds < recentSeconds) {
    throw new Error("Rider Online TTL must be greater than or equal to Recent Seconds.");
  }
  if (!Number.isInteger(maxCandidates) || maxCandidates < 1) {
    throw new Error("Maximum Candidate Drivers must be a whole number greater than 0.");
  }
  if (!Number.isInteger(maxTotal) || maxTotal < 1) {
    throw new Error("Maximum Total Assigned Orders must be a whole number greater than 0.");
  }
  if (!Number.isInteger(maxFood) || maxFood < 1 || maxFood > maxTotal) {
    throw new Error("Maximum Food Assigned Orders must be between 1 and the Maximum Total Assigned Orders.");
  }
  if (!Number.isInteger(maxGrocery) || maxGrocery < 1 || maxGrocery > maxTotal) {
    throw new Error("Maximum Grocery Assigned Orders must be between 1 and the Maximum Total Assigned Orders.");
  }
  if (maxFood + maxGrocery > maxTotal) {
    throw new Error(
      `Food (${maxFood}) + Grocery (${maxGrocery}) assigned-order limits cannot exceed Maximum Total Assigned Orders (${maxTotal}).`
    );
  }

  const reservationMinutes = integerValue("foodDriverReservationMinutes");
  if (!Number.isInteger(reservationMinutes) || reservationMinutes < 1 || reservationMinutes > 1440) {
    throw new Error("Food Driver Reservation Time must be between 1 and 1440 minutes.");
  }

  const retryInterval = integerValue("dispatchRetryIntervalSeconds");
  const retryStaleAfter = integerValue("dispatchRetryStaleAfterSeconds");
  const retryMaxAge = integerValue("dispatchRetryMaxAgeMinutes");
  const retryBatchSize = integerValue("dispatchRetryBatchSize");
  const retryMaxAttempts = integerValue("dispatchRetryMaxAttempts");

  if (!Number.isInteger(retryInterval) || retryInterval < 60) {
    throw new Error("Retry Interval must be at least 60 seconds.");
  }
  if (!Number.isInteger(retryStaleAfter) || retryStaleAfter < 10) {
    throw new Error("Retry When Stale After must be at least 10 seconds.");
  }
  if (!Number.isInteger(retryMaxAge) || retryMaxAge < 5) {
    throw new Error("Maximum Retry Age must be at least 5 minutes.");
  }
  if (!Number.isInteger(retryBatchSize) || retryBatchSize < 1 || retryBatchSize > 100) {
    throw new Error("Retry Batch Size must be between 1 and 100.");
  }
  if (!Number.isInteger(retryMaxAttempts) || retryMaxAttempts < 1) {
    throw new Error("Maximum Attempts must be a whole number greater than 0.");
  }
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[22px] border bg-white shadow-[0_8px_30px_rgba(15,23,42,0.05)] ${className}`}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section
      className="h-full rounded-[20px] border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03)] sm:p-6"
    >
      <div className="mb-5 flex items-start gap-3.5 border-b border-slate-100 pb-4">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-rose-50 text-rose-600">
          <Icon size={19} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold leading-5 text-slate-900">{title}</h3>
          {description ? (
            <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function InfoTip({ text }) {
  if (!text) return null;

  return (
    <span className="group relative inline-flex shrink-0">
      <button
        type="button"
        className="grid h-[18px] w-[18px] place-items-center rounded-full border border-slate-200 bg-white text-slate-400 transition hover:border-rose-200 hover:text-rose-500"
        aria-label="Field information"
      >
        <Info size={12} />
      </button>
      <span className="pointer-events-none absolute left-1/2 top-6 z-40 hidden w-[260px] -translate-x-1/2 rounded-[12px] border border-slate-200 bg-slate-900 px-3 py-2 text-[11px] font-medium leading-5 text-white shadow-xl group-hover:block">
        {text}
      </span>
    </span>
  );
}

function Field({ label, hint, info, children }) {
  return (
    <div className="flex min-w-0 flex-col">
      <div className="mb-2 flex min-h-[42px] flex-col justify-end gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <label className="text-[13px] font-semibold leading-5 text-slate-700">
            {label}
          </label>
          <InfoTip text={info} />
        </div>
        {hint ? (
          <span className="text-[11px] leading-4 text-slate-400">{hint}</span>
        ) : (
          <span className="h-4" aria-hidden="true" />
        )}
      </div>
      {children}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder = "",
  type = "text",
  min,
  max,
  disabled = false,
}) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      min={min}
      max={max}
      disabled={disabled}
      inputMode={type === "number" ? "decimal" : undefined}
      step={type === "number" ? "any" : undefined}
      className={`h-11 w-full rounded-[12px] border px-3.5 text-sm outline-none transition focus:border-rose-300 focus:ring-4 focus:ring-rose-50 ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300"
      }`}
    />
  );
}

function SelectInput({ value, onChange, options = [], disabled = false }) {
  return (
    <select
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      className={`h-11 w-full rounded-[12px] border px-3.5 text-sm outline-none transition focus:border-rose-300 focus:ring-4 focus:ring-rose-50 ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
          : "border-slate-200 bg-white text-slate-800 hover:border-slate-300"
      }`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function WarehouseCheckboxList({ value = [], onChange, options = [] }) {
  const selected = Array.isArray(value) ? value.map(String) : [];

  const toggleOne = (id) => {
    const sid = String(id);
    const next = selected.includes(sid)
      ? selected.filter((x) => x !== sid)
      : [...selected, sid];

    onChange(next);
  };

  const allIds = options.map((o) => String(o.value));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  return (
    <div
      className="rounded-[16px] border bg-white p-3"
      style={{ borderColor: BORDER }}
    >
      <label className="mb-3 flex items-center gap-2 border-b pb-3 text-sm font-semibold text-slate-700"
        style={{ borderColor: BORDER }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => onChange(allSelected ? [] : allIds)}
        />
        Select All Warehouses
      </label>

      <div className="grid max-h-[180px] gap-2 overflow-y-auto pr-1 md:grid-cols-2">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 rounded-[12px] px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <input
              type="checkbox"
              checked={selected.includes(String(option.value))}
              onChange={() => toggleOne(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div className="flex min-h-14 items-center justify-between gap-4 rounded-[14px] border border-slate-200 bg-slate-50/60 px-4 py-3">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition focus:outline-none focus:ring-4 focus:ring-rose-100 ${
          checked ? "bg-[#d86a78]" : "bg-slate-300"
        }`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
            checked ? "left-6" : "left-1"
          }`}
        />
      </button>
    </div>
  );
}

function StatusPopup({
  open,
  type = "success",
  title,
  message,
  buttonText = "OK",
  onClose,
}) {
  if (!open) return null;

  const isSuccess = type === "success";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/35 px-4">
      <div
        className="relative w-full max-w-[540px] rounded-[24px] border bg-white px-6 py-7 shadow-2xl"
        style={{ borderColor: BORDER }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-sm font-semibold text-[#d86a78] transition hover:opacity-80"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div
            className={`mb-4 rounded-full p-3 ${isSuccess ? "bg-green-100" : "bg-red-100"
              }`}
          >
            {isSuccess ? (
              <CheckCircle2 size={28} className="text-green-600" />
            ) : (
              <AlertCircle size={28} className="text-red-600" />
            )}
          </div>

          <h3 className="text-[28px] font-bold text-slate-900">{title}</h3>

          <p className="mt-3 max-w-[360px] text-sm leading-6 text-slate-500">
            {message}
          </p>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={onClose}
              className="min-w-[120px] rounded-[10px] px-6 py-2.5 text-sm font-bold text-white transition"
              style={{ backgroundColor: PINK }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = PINK_HOVER)
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = PINK)
              }
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PRESETS = [
  {
    key: "charges",
    title: "Charges & Pricing",
    description: "Customer charges, restaurant POD and driver earning rules",
    icon: Coins,
  },
  {
    key: "delivery",
    title: "Delivery Settings",
    description: "Delivery, partner assignment and lookup defaults",
    icon: Truck,
  },
  {
    key: "userAuth",
    title: "User Auth Settings",
    description: "Customer email login/signup switch",
    icon: ShieldCheck,
  },
  {
    key: "scheduler",
    title: "Scheduler Settings",
    description: "Cron expressions for background jobs",
    icon: Settings2,
  },
  {
    key: "warehouseDefaults",
    title: "Warehouse Defaults",
    description: "Warehouse timings and stock alert defaults",
    icon: Warehouse,
  },
  {
    key: "deliveryIncentives",
    title: "Delivery Incentives",
    description: "Daily, weekly and monthly incentive slab controls",
    icon: Coins,
  },
  {
    key: "reportRetention",
    title: "Data Archive & Retention",
    description: "Archive old audit/log data to MinIO, verify it, then purge DB rows",
    icon: CalendarRange,
  },
];

const PRESET_META = PRESETS.reduce((acc, preset) => {
  acc[preset.key] = preset;
  return acc;
}, {});

const PRESET_GROUPS = {
  delivery: "delivery",
  charges: "delivery",
  userAuth: "auth",
  scheduler: "scheduler",
  warehouseDefaults: "warehouse",
  deliveryIncentives: "delivery",
  reportRetention: "reports",
};

function getStoragePresetKey(presetKey) {
  return presetKey === "charges" ? "delivery" : presetKey;
}

function makeSettingStoragePayload(presetKey, settingValue) {
  const meta = PRESET_META[presetKey] || {};
  const group = PRESET_GROUPS[presetKey] || presetKey;

  return {
    settingKey: presetKey,
    setting_key: presetKey,
    key: presetKey,
    settingGroup: group,
    setting_group: group,
    group,
    title: meta.title || presetKey,
    description: meta.description || "",
    settingValue,
    setting_value: settingValue,
    rawSettingValue: settingValue,
    raw_setting_value: settingValue,
    value: settingValue,
    data: settingValue,
    isPublic: false,
    is_public: false,
    isActive: true,
    is_active: true,
  };
}

const ARCHIVE_TABLE_FIELDS = [
  { key: "activity_logs", label: "Activity Logs", hint: "Admin/security activity history" },
  { key: "platform_logs", label: "Platform Logs", hint: "Platform diagnostic/audit logs" },
  { key: "driver_availability_audit", label: "Driver Availability Audit", hint: "Historical driver availability checks" },
  { key: "user_serviceability_logs", label: "User Serviceability Logs", hint: "Stale per-user serviceability snapshots" },
  { key: "google_api_usage_events", label: "Google API Usage Events", hint: "Raw Google API usage events" },
  { key: "delivery_partner_location_history", label: "Delivery Partner Location History", hint: "Historical driver GPS points" },
  { key: "delivery_task_events", label: "Delivery Task Events", hint: "Historical delivery task timeline events" },
  { key: "short_video_user_events", label: "Short Video User Events", hint: "Raw view/click/complete/chunk events" },
  { key: "short_video_admin_history", label: "Short Video Admin History", hint: "Admin changes made to short videos" },
];

const DEFAULT_STATE = {
  delivery: {
    cartHoldMinutes: 5,
    // Hyperlocal delivery defaults: keep caches short and rider freshness tight.
    serviceabilityTtlSeconds: 30,
    deliveryPartnerTtlSeconds: 15,
    productAvailabilityTtlSeconds: 30,
    searchCityFallback: true,
    defaultOrderKm: 8,
    // Legacy shared radius values stay in state for backward-compatible saves.
    partnerSearchKm: 5,
    maxPartnerRadiusKm: 15,
    groceryInitialSearchKm: 5,
    groceryMaxSearchKm: 15,
    foodInitialSearchKm: 5,
    foodMaxSearchKm: 15,
    recentSeconds: 90,
    activeStatuses: ["Active", "ACTIVE"],
    approvalStatuses: ["APPROVED"],
    riderOnlineTtlSeconds: 120,

    maxCandidates: 25,
    defaultMaxActiveOrders: 5,
    maxFoodOrdersPerPartner: 2,
    maxGroceryOrdersPerPartner: 3,
    blockAssignmentWhileFoodPickupPending: true,
    foodDriverReservationMinutes: 5,

    dispatchRetryEnabled: true,
    dispatchRetryIntervalSeconds: 120,
    dispatchRetryStaleAfterSeconds: 60,
    dispatchRetryMaxAgeMinutes: 180,
    dispatchRetryBatchSize: 25,
    dispatchRetryMaxAttempts: 30,

    groceryMinimumOrderValue: 0,
    grocerySmallCartChargeEnabled: false,
    grocerySmallCartChargeAmount: 0,

    deliveryFeeEnabled: true,
    deliveryFeeCurrency: "INR",
    deliveryFeeBaseKm: 3,
    deliveryFeeBaseFee: 20,
    deliveryFeePerKmCharge: 8,
    deliveryFeeMinimumFee: 0,
    deliveryFeeMaxFee: 80,
    deliveryFeeFreeDeliveryAbove: 499,
    deliveryFeeRoundTo: 1,
    deliveryFeeWarehouseOverrides: [],

    driverEarningEnabled: true,
    driverEarningMode: "DISTANCE_BASED",
    driverEarningCurrency: "INR",
    driverEarningFixedAmount: 35,
    driverEarningBaseKm: 3,
    driverEarningBaseFee: 25,
    driverEarningPerKmCharge: 8,
    driverEarningMinimumFee: 25,
    driverEarningMaxFee: 100,
    driverEarningRoundTo: 1,
    driverEarningWarehouseOverrides: [],

    handlingChargeEnabled: false,
    handlingChargeCurrency: "INR",
    handlingChargeFreeUpToKg: 0,
    handlingChargeBaseCharge: 0,
    handlingChargePerKgCharge: 0,
    handlingChargeMinimumCharge: 0,
    handlingChargeMaxCharge: 0,
    handlingChargeRoundWeightToKg: 0.5,
    handlingChargeRoundTo: 1,
    handlingChargeDefaultItemWeightKg: 0,
    handlingChargeWarehouseOverrides: [],

    rainChargeEnabled: false,
    rainChargeActivationMode: "AUTO",
    rainChargeCurrency: "INR",
    rainChargeAmount: 0,
    rainChargeMinOrderValue: 0,
    rainChargeMinProbabilityPercent: 50,
    rainChargeCacheTtlSeconds: 300,
    rainChargeWarehouseOverrides: [],

    surgeChargeEnabled: false,
    surgeChargeActivationMode: "AUTO",
    surgeChargeAmount: 0,
    surgeChargeActiveOrderThreshold: 20,
    surgeChargeFreeAboveOrderAmount: 0,
    surgeChargeWarehouseOverrides: [],

    payOnDeliveryEnabled: false,
    payOnDeliveryProvider: "RAZORPAY",
    payOnDeliveryWarehouseOverrides: [],
    payOnDeliveryRestaurantOverrides: [],
  },
  userAuth: {
    emailAuthEnabled: true,
    phoneAuthEnabled: true,
  },
  scheduler: {
    orderAutoCancelEvery: 10,
    orderAutoCancelUnit: "minutes",
    deliveryReassignEvery: 5,
    deliveryReassignUnit: "minutes",
    stockSyncTime: "09:00",
    stockAlertsTime: "09:00",
    notificationRetryEvery: 15,
    notificationRetryUnit: "minutes",
    cartHoldCleanupEvery: 1,
    cartHoldCleanupUnit: "minutes",
    dataArchiveTime: "02:30",
  },
  warehouseDefaults: {
    expiryAlertDays: 30,
    lowStockThreshold: 10,
    timezone: "Asia/Kolkata",
    openingTime: "09:00",
    closingTime: "22:00",
  },
  deliveryIncentives: {
    enabled: false,
    currency: "INR",
    stackingRule: "top_up",

    selectedState: "",
    selectedWarehouseId: "",
    selectedWarehouseIds: [],

    dailyEnabled: true,
    dailyResetTimezone: "Asia/Kolkata",
    dailySlabs: [],
    globalDailySlabs: [],

    weeklyEnabled: true,
    weeklyWeekStartsOn: "MONDAY",
    weeklySlabs: [],
    globalWeeklySlabs: [],

    monthlyEnabled: true,
    monthlySlabs: [],
    globalMonthlySlabs: [],

    warehouseOverrides: [],
  },
  reportRetention: {
    enabled: false,
    batchSize: 10000,
    maxPartsPerTablePerRun: 10,
    tables: {
      activity_logs: { enabled: true, retentionDays: 365 },
      platform_logs: { enabled: true, retentionDays: 90 },
      driver_availability_audit: { enabled: true, retentionDays: 60 },
      user_serviceability_logs: { enabled: true, retentionDays: 90 },
      google_api_usage_events: { enabled: true, retentionDays: 90 },
      delivery_partner_location_history: { enabled: true, retentionDays: 60 },
      delivery_task_events: { enabled: true, retentionDays: 180 },
      short_video_user_events: { enabled: true, retentionDays: 90 },
      short_video_admin_history: { enabled: true, retentionDays: 365 },
    },
  },
};

function normalizeSettingResponse(res) {
  if (!res) return null;
  if (res?.item) return res.item;
  if (res?.data?.item) return res.data.item;
  if (res?.data) return res.data;
  return res;
}

function parseNumberField(value) {
  if (value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return Math.max(0, num);
}

function normalizeBooleanSetting(value, fallback = false) {
  if (value == null || value === "") return Boolean(fallback);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
}

function normalizeNonNegativeNumber(value, fallback = 0) {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return Math.max(0, parsed);

  const fallbackNumber = Number(fallback);
  return Number.isFinite(fallbackNumber) ? Math.max(0, fallbackNumber) : 0;
}

function normalizeIncentiveSlabs(slabs) {
  if (!Array.isArray(slabs)) return [];

  return slabs
    .map((item) => ({
      minDeliveries: Math.max(0, Number(item?.minDeliveries || 0)),
      amount: Math.max(0, Number(item?.amount || 0)),
    }))
    .filter((item) => item.minDeliveries > 0)
    .sort((a, b) => a.minDeliveries - b.minDeliveries);
}

function addEmptySlab(list = []) {
  return [...list, { minDeliveries: "", amount: "" }];
}

function normalizeDeliveryFeeWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((item) => ({
      warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
      warehouseName: item?.warehouseName || item?.warehouse_name || "",
      enabled: item?.enabled ?? true,
      baseKm: Math.max(0, Number(item?.baseKm || 0)),
      baseFee: Math.max(0, Number(item?.baseFee || 0)),
      perKmCharge: Math.max(0, Number(item?.perKmCharge || 0)),
      minimumFee: Math.max(0, Number(item?.minimumFee || 0)),
      maxFee: Math.max(0, Number(item?.maxFee || 0)),
      freeDeliveryAbove: Math.max(0, Number(item?.freeDeliveryAbove || 0)),
      roundTo: Math.max(0, Number(item?.roundTo || 1)),
    }))
    .filter((item) => item.warehouseId);
}

function addEmptyDeliveryFeeWarehouseOverride(list = []) {
  return [
    ...list,
    {
      warehouseId: "",
      warehouseName: "",
      enabled: true,
      baseKm: 3,
      baseFee: 20,
      perKmCharge: 8,
      minimumFee: 0,
      maxFee: 80,
      freeDeliveryAbove: 499,
      roundTo: 1,
    },
  ];
}



function normalizeDriverEarningMode(value, fallback = "DISTANCE_BASED") {
  const text = String(value || fallback || "DISTANCE_BASED").trim().toUpperCase();
  if (["FIXED", "FIX", "FLAT", "FLAT_RATE"].includes(text)) return "FIXED";
  return "DISTANCE_BASED";
}

function normalizeDriverEarningWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((item) => ({
      warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
      warehouseName: item?.warehouseName || item?.warehouse_name || "",
      enabled: item?.enabled ?? true,
      mode: normalizeDriverEarningMode(item?.mode),
      fixedAmount: Math.max(0, Number(item?.fixedAmount ?? item?.fixed_amount ?? item?.amount ?? 35)),
      baseKm: Math.max(0, Number(item?.baseKm ?? item?.base_km ?? 3)),
      baseFee: Math.max(0, Number(item?.baseFee ?? item?.base_fee ?? 25)),
      perKmCharge: Math.max(0, Number(item?.perKmCharge ?? item?.per_km_charge ?? 8)),
      minimumFee: Math.max(0, Number(item?.minimumFee ?? item?.minimum_fee ?? 25)),
      maxFee: Math.max(0, Number(item?.maxFee ?? item?.max_fee ?? 100)),
      roundTo: Math.max(0, Number(item?.roundTo ?? item?.round_to ?? 1)),
    }))
    .filter((item) => item.warehouseId);
}

function addEmptyDriverEarningWarehouseOverride(list = []) {
  return [
    ...list,
    {
      warehouseId: "",
      warehouseName: "",
      enabled: true,
      mode: "DISTANCE_BASED",
      fixedAmount: 35,
      baseKm: 3,
      baseFee: 25,
      perKmCharge: 8,
      minimumFee: 25,
      maxFee: 100,
      roundTo: 1,
    },
  ];
}

function normalizeHandlingChargeWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];
  return overrides.map((item) => ({
    warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
    warehouseName: item?.warehouseName || item?.warehouse_name || "",
    enabled: item?.enabled ?? true,
    freeUpToKg: Math.max(0, Number(item?.freeUpToKg ?? item?.free_up_to_kg ?? 0)),
    baseCharge: Math.max(0, Number(item?.baseCharge ?? item?.base_charge ?? 0)),
    perKgCharge: Math.max(0, Number(item?.perKgCharge ?? item?.per_kg_charge ?? 0)),
    minimumCharge: Math.max(0, Number(item?.minimumCharge ?? item?.minimum_charge ?? 0)),
    maxCharge: Math.max(0, Number(item?.maxCharge ?? item?.max_charge ?? 0)),
    roundWeightToKg: Math.max(0, Number(item?.roundWeightToKg ?? item?.round_weight_to_kg ?? 0.5)),
    roundTo: Math.max(0, Number(item?.roundTo ?? item?.round_to ?? 1)),
    defaultItemWeightKg: Math.max(0, Number(item?.defaultItemWeightKg ?? item?.default_item_weight_kg ?? 0)),
  })).filter((item) => item.warehouseId);
}

function addEmptyHandlingChargeWarehouseOverride(list = []) {
  return [...list, { warehouseId: "", warehouseName: "", enabled: true, freeUpToKg: 0, baseCharge: 0, perKgCharge: 0, minimumCharge: 0, maxCharge: 0, roundWeightToKg: 0.5, roundTo: 1, defaultItemWeightKg: 0 }];
}

function normalizeActivationMode(value) {
  return String(value || "AUTO").trim().toUpperCase() === "FORCE_ON" ? "FORCE_ON" : "AUTO";
}

function normalizeRainChargeWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((item) => ({
      warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
      warehouseName: item?.warehouseName || item?.warehouse_name || "",
      enabled: item?.enabled ?? false,
      activationMode: normalizeActivationMode(item?.activationMode ?? item?.activation_mode),
      currency: "INR",
      amount: Math.max(0, Number(item?.amount || item?.charge || 0)),
      minOrderValue: Math.max(0, Number(item?.minOrderValue ?? item?.min_order_value ?? 0)),
      minProbabilityPercent: Math.min(100, Math.max(0, Number(item?.minProbabilityPercent ?? item?.min_probability_percent ?? 50))),
      cacheTtlSeconds: Math.max(30, Number(item?.cacheTtlSeconds ?? item?.cache_ttl_seconds ?? 300)),
    }))
    .filter((item) => item.warehouseId);
}

function addEmptyRainChargeWarehouseOverride(list = []) {
  return [
    ...list,
    {
      warehouseId: "",
      warehouseName: "",
      enabled: false,
      activationMode: "AUTO",
      currency: "INR",
      amount: 0,
      minOrderValue: 0,
      minProbabilityPercent: 50,
      cacheTtlSeconds: 300,
    },
  ];
}

function normalizeSurgeChargeWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];
  return overrides.map((item) => ({
    warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
    warehouseName: item?.warehouseName || item?.warehouse_name || "",
    enabled: item?.enabled ?? true,
    activationMode: normalizeActivationMode(item?.activationMode ?? item?.activation_mode),
    amount: Math.max(0, Number(item?.amount ?? 0)),
    activeOrderThreshold: Math.max(1, Math.trunc(Number(item?.activeOrderThreshold ?? item?.active_order_threshold ?? 20) || 20)),
    freeAboveOrderAmount: Math.max(0, Number(item?.freeAboveOrderAmount ?? item?.free_above_order_amount ?? 0)),
  })).filter((item) => item.warehouseId);
}

function addEmptySurgeChargeWarehouseOverride(list = []) {
  return [...list, { warehouseId: "", warehouseName: "", enabled: true, activationMode: "AUTO", amount: 0, activeOrderThreshold: 20, freeAboveOrderAmount: 0 }];
}

function normalizePayOnDeliveryWarehouseOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((item) => ({
      warehouseId: String(item?.warehouseId || item?.warehouse_id || "").trim(),
      warehouseName: item?.warehouseName || item?.warehouse_name || "",
      enabled: item?.enabled ?? true,
      ...(item?.companyOnlineEnabled !== undefined || item?.company_online_enabled !== undefined
        ? { companyOnlineEnabled: item?.companyOnlineEnabled ?? item?.company_online_enabled }
        : {}),
      ...(item?.driverCashEnabled !== undefined || item?.driver_cash_enabled !== undefined
        ? { driverCashEnabled: item?.driverCashEnabled ?? item?.driver_cash_enabled }
        : {}),
      ...(item?.driverSplitEnabled !== undefined || item?.driver_split_enabled !== undefined
        ? { driverSplitEnabled: item?.driverSplitEnabled ?? item?.driver_split_enabled }
        : {}),
    }))
    .filter((item) => item.warehouseId);
}

function addEmptyPayOnDeliveryWarehouseOverride(list = []) {
  return [
    ...list,
    {
      warehouseId: "",
      warehouseName: "",
      // Warehouse override is mainly used to block POD at a selected
      // warehouse while the global module remains enabled.
      enabled: false,
    },
  ];
}

function normalizePayOnDeliveryRestaurantOverrides(overrides) {
  if (!Array.isArray(overrides)) return [];

  return overrides
    .map((item) => ({
      restaurantId: String(item?.restaurantId || item?.restaurant_id || "").trim(),
      restaurantName: item?.restaurantName || item?.restaurant_name || item?.name || "",
      enabled: item?.enabled ?? true,
      ...(item?.companyOnlineEnabled !== undefined || item?.company_online_enabled !== undefined
        ? { companyOnlineEnabled: item?.companyOnlineEnabled ?? item?.company_online_enabled }
        : {}),
      ...(item?.driverCashEnabled !== undefined || item?.driver_cash_enabled !== undefined
        ? { driverCashEnabled: item?.driverCashEnabled ?? item?.driver_cash_enabled }
        : {}),
      ...(item?.driverSplitEnabled !== undefined || item?.driver_split_enabled !== undefined
        ? { driverSplitEnabled: item?.driverSplitEnabled ?? item?.driver_split_enabled }
        : {}),
    }))
    .filter((item) => item.restaurantId);
}

function addEmptyPayOnDeliveryRestaurantOverride(list = []) {
  return [
    ...list,
    {
      restaurantId: "",
      restaurantName: "",
      enabled: true,
    },
  ];
}

function assertPayOnDeliveryPersisted(expected = {}, saved = {}) {
  const expectedEnabled = !!expected.payOnDeliveryEnabled;
  const savedEnabled = !!saved.payOnDeliveryEnabled;
  const expectedProvider = String(expected.payOnDeliveryProvider || "RAZORPAY").trim().toUpperCase();
  const savedProvider = String(saved.payOnDeliveryProvider || "RAZORPAY").trim().toUpperCase();

  const expectedWarehouseOverrides = normalizePayOnDeliveryWarehouseOverrides(
    expected.payOnDeliveryWarehouseOverrides
  );
  const savedWarehouseOverrides = normalizePayOnDeliveryWarehouseOverrides(
    saved.payOnDeliveryWarehouseOverrides
  );
  const expectedRestaurantOverrides = normalizePayOnDeliveryRestaurantOverrides(
    expected.payOnDeliveryRestaurantOverrides
  );
  const savedRestaurantOverrides = normalizePayOnDeliveryRestaurantOverrides(
    saved.payOnDeliveryRestaurantOverrides
  );

  if (
    savedEnabled !== expectedEnabled ||
    savedProvider !== expectedProvider ||
    JSON.stringify(savedWarehouseOverrides) !== JSON.stringify(expectedWarehouseOverrides) ||
    JSON.stringify(savedRestaurantOverrides) !== JSON.stringify(expectedRestaurantOverrides)
  ) {
    throw new Error(
      "Settings API accepted the request but did not persist the Pay on Delivery configuration."
    );
  }
}

function makeIncentivePeriod({ enabled = true, resetTimezone, weekStartsOn, slabs = [] } = {}) {
  return {
    enabled,
    ...(resetTimezone ? { resetTimezone } : {}),
    ...(weekStartsOn ? { weekStartsOn } : {}),
    slabs: normalizeIncentiveSlabs(slabs),
  };
}

function summarizeSlabs(slabs = []) {
  if (!Array.isArray(slabs) || !slabs.length) return "Default";
  return slabs.map((s) => `${s.minDeliveries} = ₹${s.amount}`).join(", ");
}

function buildCurrentIncentiveOverride(values, warehouseOptions = []) {
  if (!values.selectedWarehouseId) return null;


  const hasAnySlab =
    normalizeIncentiveSlabs(values.dailySlabs).length > 0 ||
    normalizeIncentiveSlabs(values.weeklySlabs).length > 0 ||
    normalizeIncentiveSlabs(values.monthlySlabs).length > 0;

  if (!hasAnySlab) return null;

  const selectedWarehouse = warehouseOptions.find(
    (w) => String(w.id) === String(values.selectedWarehouseId)
  );

  return {
    state: selectedWarehouse?.state || values.selectedState || null,
    city: selectedWarehouse?.city || null,
    warehouseId: values.selectedWarehouseId,
    warehouseName: selectedWarehouse?.name || "",
    enabled: !!values.enabled,
    stackingRule: values.stackingRule || "top_up",

    daily: makeIncentivePeriod({
      enabled: !!values.dailyEnabled,
      resetTimezone: values.dailyResetTimezone || "Asia/Kolkata",
      slabs: values.dailySlabs,
    }),

    weekly: makeIncentivePeriod({
      enabled: !!values.weeklyEnabled,
      weekStartsOn: values.weeklyWeekStartsOn || "MONDAY",
      slabs: values.weeklySlabs,
    }),

    monthly: makeIncentivePeriod({
      enabled: !!values.monthlyEnabled,
      slabs: values.monthlySlabs,
    }),
  };
}

function upsertCurrentIncentiveOverride(values, warehouseOptions = []) {
  const current = buildCurrentIncentiveOverride(values, warehouseOptions);
  if (!current) return values.warehouseOverrides || [];

  const list = Array.isArray(values.warehouseOverrides)
    ? values.warehouseOverrides
    : [];

  return [
    ...list.filter((item) => String(item.warehouseId) !== String(current.warehouseId)),
    current,
  ];
}

function buildIncentiveOverrideForWarehouse(values, warehouse) {
  if (!warehouse?.id) return null;

  const hasAnySlab =
    normalizeIncentiveSlabs(values.dailySlabs).length > 0 ||
    normalizeIncentiveSlabs(values.weeklySlabs).length > 0 ||
    normalizeIncentiveSlabs(values.monthlySlabs).length > 0;

  if (!hasAnySlab) return null;

  return {
    state: warehouse.state || values.selectedState || null,
    city: warehouse.city || null,
    warehouseId: warehouse.id,
    warehouseName: warehouse.name || "",
    enabled: !!values.enabled,
    stackingRule: values.stackingRule || "top_up",

    daily: makeIncentivePeriod({
      enabled: !!values.dailyEnabled,
      resetTimezone: values.dailyResetTimezone || "Asia/Kolkata",
      slabs: values.dailySlabs,
    }),

    weekly: makeIncentivePeriod({
      enabled: !!values.weeklyEnabled,
      weekStartsOn: values.weeklyWeekStartsOn || "MONDAY",
      slabs: values.weeklySlabs,
    }),

    monthly: makeIncentivePeriod({
      enabled: !!values.monthlyEnabled,
      slabs: values.monthlySlabs,
    }),
  };
}

function upsertBulkIncentiveOverrides(values, warehouseOptions = []) {
  const ids = Array.isArray(values.selectedWarehouseIds)
    ? values.selectedWarehouseIds
    : [];

  if (!ids.length) {
    return upsertCurrentIncentiveOverride(values, warehouseOptions);
  }

  const list = Array.isArray(values.warehouseOverrides)
    ? values.warehouseOverrides
    : [];

  const selectedWarehouses = warehouseOptions.filter((w) =>
    ids.includes(String(w.id))
  );

  const newOverrides = selectedWarehouses
    .map((warehouse) => buildIncentiveOverrideForWarehouse(values, warehouse))
    .filter(Boolean);

  const newIds = new Set(newOverrides.map((item) => String(item.warehouseId)));

  return [
    ...list.filter((item) => !newIds.has(String(item.warehouseId))),
    ...newOverrides,
  ];
}

function normalizeIncomingPreset(presetKey, item) {
  const incoming =
    item?.setting_value?.data ||
    item?.setting_value ||
    item?.value?.data ||
    item?.value ||
    item?.payload?.data ||
    item?.payload ||
    item?.data ||
    {};

  if (presetKey === "userAuth") {
    const emailAuthEnabled =
      incoming?.emailAuthEnabled ??
      incoming?.email_auth_enabled ??
      incoming?.auth?.emailAuthEnabled ??
      incoming?.login?.allowEmail ??
      incoming?.signup?.allowEmail ??
      DEFAULT_STATE.userAuth.emailAuthEnabled;

    const phoneAuthEnabled =
      incoming?.phoneAuthEnabled ??
      incoming?.phone_auth_enabled ??
      incoming?.auth?.phoneAuthEnabled ??
      incoming?.login?.allowPhone ??
      incoming?.signup?.allowPhone ??
      DEFAULT_STATE.userAuth.phoneAuthEnabled;

    return {
      ...DEFAULT_STATE.userAuth,
      emailAuthEnabled: emailAuthEnabled !== false,
      phoneAuthEnabled: phoneAuthEnabled !== false,
    };
  }

  if (presetKey === "delivery") {
    return {
      ...DEFAULT_STATE.delivery,
      cartHoldMinutes:
        incoming?.cart?.holdMinutes ?? DEFAULT_STATE.delivery.cartHoldMinutes,
      serviceabilityTtlSeconds:
        incoming?.cache?.serviceabilityTtlSeconds ??
        DEFAULT_STATE.delivery.serviceabilityTtlSeconds,
      deliveryPartnerTtlSeconds:
        incoming?.cache?.deliveryPartnerTtlSeconds ??
        DEFAULT_STATE.delivery.deliveryPartnerTtlSeconds,
      productAvailabilityTtlSeconds:
        incoming?.cache?.productAvailabilityTtlSeconds ??
        DEFAULT_STATE.delivery.productAvailabilityTtlSeconds,
      searchCityFallback:
        incoming?.lookup?.searchCityFallback ??
        DEFAULT_STATE.delivery.searchCityFallback,
      defaultOrderKm:
        incoming?.radius?.defaultOrderKm ?? DEFAULT_STATE.delivery.defaultOrderKm,
      partnerSearchKm:
        incoming?.radius?.partnerSearchKm ??
        incoming?.radius?.groceryInitialSearchKm ??
        DEFAULT_STATE.delivery.partnerSearchKm,
      maxPartnerRadiusKm:
        incoming?.radius?.maxPartnerRadiusKm ??
        incoming?.radius?.groceryMaxSearchKm ??
        DEFAULT_STATE.delivery.maxPartnerRadiusKm,
      groceryInitialSearchKm:
        incoming?.radius?.groceryInitialSearchKm ??
        incoming?.GROCERY_INITIAL_PICKUP_SEARCH_RADIUS_KM ??
        incoming?.radius?.partnerSearchKm ??
        DEFAULT_STATE.delivery.groceryInitialSearchKm,
      groceryMaxSearchKm:
        incoming?.radius?.groceryMaxSearchKm ??
        incoming?.GROCERY_MAX_PICKUP_SEARCH_RADIUS_KM ??
        incoming?.radius?.maxPartnerRadiusKm ??
        DEFAULT_STATE.delivery.groceryMaxSearchKm,
      foodInitialSearchKm:
        incoming?.radius?.foodInitialSearchKm ??
        incoming?.FOOD_INITIAL_PICKUP_SEARCH_RADIUS_KM ??
        incoming?.radius?.partnerSearchKm ??
        DEFAULT_STATE.delivery.foodInitialSearchKm,
      foodMaxSearchKm:
        incoming?.radius?.foodMaxSearchKm ??
        incoming?.FOOD_MAX_PICKUP_SEARCH_RADIUS_KM ??
        incoming?.radius?.maxPartnerRadiusKm ??
        DEFAULT_STATE.delivery.foodMaxSearchKm,
      recentSeconds:
        incoming?.partner?.recentSeconds ?? DEFAULT_STATE.delivery.recentSeconds,
      activeStatuses: [...DEFAULT_STATE.delivery.activeStatuses],
      approvalStatuses: [...DEFAULT_STATE.delivery.approvalStatuses],
      riderOnlineTtlSeconds:
        incoming?.partner?.riderOnlineTtlSeconds ??
        DEFAULT_STATE.delivery.riderOnlineTtlSeconds,
      maxCandidates:
        incoming?.assignment?.maxCandidates ??
        incoming?.MAX_ASSIGNMENT_CANDIDATES ??
        DEFAULT_STATE.delivery.maxCandidates,
      defaultMaxActiveOrders:
        incoming?.assignment?.defaultMaxActiveOrders ??
        incoming?.MAX_ACTIVE_ORDERS_PER_PARTNER ??
        DEFAULT_STATE.delivery.defaultMaxActiveOrders,
      maxFoodOrdersPerPartner:
        incoming?.assignment?.maxFoodOrdersPerPartner ??
        incoming?.MAX_FOOD_ORDERS_PER_PARTNER ??
        DEFAULT_STATE.delivery.maxFoodOrdersPerPartner,
      maxGroceryOrdersPerPartner:
        incoming?.assignment?.maxGroceryOrdersPerPartner ??
        incoming?.MAX_GROCERY_ORDERS_PER_PARTNER ??
        DEFAULT_STATE.delivery.maxGroceryOrdersPerPartner,
      blockAssignmentWhileFoodPickupPending:
        incoming?.assignment?.blockAssignmentWhileFoodPickupPending ??
        incoming?.BLOCK_ASSIGNMENT_WHILE_FOOD_PICKUP_PENDING ??
        DEFAULT_STATE.delivery.blockAssignmentWhileFoodPickupPending,
      foodDriverReservationMinutes:
        incoming?.assignment?.foodDriverReservationMinutes ??
        incoming?.FOOD_DRIVER_RESERVATION_MINUTES ??
        DEFAULT_STATE.delivery.foodDriverReservationMinutes,
      dispatchRetryEnabled:
        incoming?.assignment?.dispatchRetry?.enabled ??
        incoming?.DISPATCH_RETRY_ENABLED ??
        DEFAULT_STATE.delivery.dispatchRetryEnabled,
      dispatchRetryIntervalSeconds:
        incoming?.assignment?.dispatchRetry?.repeatSeconds ??
        (incoming?.DISPATCH_RETRY_REPEAT_MS != null
          ? Number(incoming.DISPATCH_RETRY_REPEAT_MS) / 1000
          : DEFAULT_STATE.delivery.dispatchRetryIntervalSeconds),
      dispatchRetryStaleAfterSeconds:
        incoming?.assignment?.dispatchRetry?.staleAfterSeconds ??
        incoming?.DISPATCH_RETRY_STALE_AFTER_SECONDS ??
        DEFAULT_STATE.delivery.dispatchRetryStaleAfterSeconds,
      dispatchRetryMaxAgeMinutes:
        incoming?.assignment?.dispatchRetry?.maxAgeMinutes ??
        incoming?.DISPATCH_RETRY_MAX_AGE_MINUTES ??
        DEFAULT_STATE.delivery.dispatchRetryMaxAgeMinutes,
      dispatchRetryBatchSize:
        incoming?.assignment?.dispatchRetry?.batchSize ??
        incoming?.DISPATCH_RETRY_BATCH_SIZE ??
        DEFAULT_STATE.delivery.dispatchRetryBatchSize,
      dispatchRetryMaxAttempts:
        incoming?.assignment?.dispatchRetry?.maxAttempts ??
        incoming?.DISPATCH_RETRY_MAX_ATTEMPTS ??
        DEFAULT_STATE.delivery.dispatchRetryMaxAttempts,

      groceryMinimumOrderValue:
        incoming?.groceryMinimumOrderValue ??
        incoming?.grocery_minimum_order_value ??
        incoming?.minimumGroceryOrderValue ??
        incoming?.minimum_grocery_order_value ??
        DEFAULT_STATE.delivery.groceryMinimumOrderValue,
      grocerySmallCartChargeEnabled: normalizeBooleanSetting(
        incoming?.grocerySmallCartCharge?.enabled ??
        incoming?.grocery_small_cart_charge?.enabled,
        DEFAULT_STATE.delivery.grocerySmallCartChargeEnabled
      ),
      grocerySmallCartChargeAmount: normalizeNonNegativeNumber(
        incoming?.grocerySmallCartCharge?.amount ??
        incoming?.grocery_small_cart_charge?.amount,
        DEFAULT_STATE.delivery.grocerySmallCartChargeAmount
      ),

      deliveryFeeEnabled:
        incoming?.deliveryFee?.enabled ?? DEFAULT_STATE.delivery.deliveryFeeEnabled,
      deliveryFeeCurrency: "INR",
      deliveryFeeBaseKm:
        incoming?.deliveryFee?.baseKm ?? DEFAULT_STATE.delivery.deliveryFeeBaseKm,
      deliveryFeeBaseFee:
        incoming?.deliveryFee?.baseFee ?? DEFAULT_STATE.delivery.deliveryFeeBaseFee,
      deliveryFeePerKmCharge:
        incoming?.deliveryFee?.perKmCharge ??
        DEFAULT_STATE.delivery.deliveryFeePerKmCharge,
      deliveryFeeMinimumFee:
        incoming?.deliveryFee?.minimumFee ??
        DEFAULT_STATE.delivery.deliveryFeeMinimumFee,
      deliveryFeeMaxFee:
        incoming?.deliveryFee?.maxFee ?? DEFAULT_STATE.delivery.deliveryFeeMaxFee,
      deliveryFeeFreeDeliveryAbove:
        incoming?.deliveryFee?.freeDeliveryAbove ??
        DEFAULT_STATE.delivery.deliveryFeeFreeDeliveryAbove,
      deliveryFeeRoundTo:
        incoming?.deliveryFee?.roundTo ?? DEFAULT_STATE.delivery.deliveryFeeRoundTo,
      deliveryFeeWarehouseOverrides: normalizeDeliveryFeeWarehouseOverrides(
        incoming?.deliveryFee?.warehouseOverrides ??
        DEFAULT_STATE.delivery.deliveryFeeWarehouseOverrides
      ),

      driverEarningEnabled:
        incoming?.driverEarningRule?.enabled ??
        incoming?.driver_earning_rule?.enabled ??
        DEFAULT_STATE.delivery.driverEarningEnabled,
      driverEarningMode:
        normalizeDriverEarningMode(
          incoming?.driverEarningRule?.mode ??
          incoming?.driver_earning_rule?.mode ??
          DEFAULT_STATE.delivery.driverEarningMode
        ),
      driverEarningCurrency: "INR",
      driverEarningFixedAmount:
        incoming?.driverEarningRule?.fixedAmount ??
        incoming?.driver_earning_rule?.fixedAmount ??
        DEFAULT_STATE.delivery.driverEarningFixedAmount,
      driverEarningBaseKm:
        incoming?.driverEarningRule?.baseKm ??
        incoming?.driver_earning_rule?.baseKm ??
        DEFAULT_STATE.delivery.driverEarningBaseKm,
      driverEarningBaseFee:
        incoming?.driverEarningRule?.baseFee ??
        incoming?.driver_earning_rule?.baseFee ??
        DEFAULT_STATE.delivery.driverEarningBaseFee,
      driverEarningPerKmCharge:
        incoming?.driverEarningRule?.perKmCharge ??
        incoming?.driver_earning_rule?.perKmCharge ??
        DEFAULT_STATE.delivery.driverEarningPerKmCharge,
      driverEarningMinimumFee:
        incoming?.driverEarningRule?.minimumFee ??
        incoming?.driver_earning_rule?.minimumFee ??
        DEFAULT_STATE.delivery.driverEarningMinimumFee,
      driverEarningMaxFee:
        incoming?.driverEarningRule?.maxFee ??
        incoming?.driver_earning_rule?.maxFee ??
        DEFAULT_STATE.delivery.driverEarningMaxFee,
      driverEarningRoundTo:
        incoming?.driverEarningRule?.roundTo ??
        incoming?.driver_earning_rule?.roundTo ??
        DEFAULT_STATE.delivery.driverEarningRoundTo,
      driverEarningWarehouseOverrides: normalizeDriverEarningWarehouseOverrides(
        incoming?.driverEarningRule?.warehouseOverrides ??
        incoming?.driver_earning_rule?.warehouseOverrides ??
        DEFAULT_STATE.delivery.driverEarningWarehouseOverrides
      ),

      handlingChargeEnabled:
        incoming?.handlingCharge?.enabled ?? DEFAULT_STATE.delivery.handlingChargeEnabled,
      handlingChargeCurrency: "INR",
      handlingChargeFreeUpToKg:
        incoming?.handlingCharge?.freeUpToKg ?? DEFAULT_STATE.delivery.handlingChargeFreeUpToKg,
      handlingChargeBaseCharge:
        incoming?.handlingCharge?.baseCharge ?? DEFAULT_STATE.delivery.handlingChargeBaseCharge,
      handlingChargePerKgCharge:
        incoming?.handlingCharge?.perKgCharge ?? DEFAULT_STATE.delivery.handlingChargePerKgCharge,
      handlingChargeMinimumCharge:
        incoming?.handlingCharge?.minimumCharge ?? DEFAULT_STATE.delivery.handlingChargeMinimumCharge,
      handlingChargeMaxCharge:
        incoming?.handlingCharge?.maxCharge ?? DEFAULT_STATE.delivery.handlingChargeMaxCharge,
      handlingChargeRoundWeightToKg:
        incoming?.handlingCharge?.roundWeightToKg ??
        DEFAULT_STATE.delivery.handlingChargeRoundWeightToKg,
      handlingChargeRoundTo:
        incoming?.handlingCharge?.roundTo ?? DEFAULT_STATE.delivery.handlingChargeRoundTo,
      handlingChargeDefaultItemWeightKg:
        incoming?.handlingCharge?.defaultItemWeightKg ??
        DEFAULT_STATE.delivery.handlingChargeDefaultItemWeightKg,
      handlingChargeWarehouseOverrides: normalizeHandlingChargeWarehouseOverrides(
        incoming?.handlingCharge?.warehouseOverrides ?? DEFAULT_STATE.delivery.handlingChargeWarehouseOverrides
      ),

      rainChargeEnabled:
        incoming?.rainCharge?.enabled ?? DEFAULT_STATE.delivery.rainChargeEnabled,
      rainChargeActivationMode: normalizeActivationMode(incoming?.rainCharge?.activationMode ?? incoming?.rainCharge?.activation_mode),
      rainChargeCurrency: "INR",
      rainChargeAmount:
        incoming?.rainCharge?.amount ?? DEFAULT_STATE.delivery.rainChargeAmount,
      rainChargeMinOrderValue:
        incoming?.rainCharge?.minOrderValue ?? DEFAULT_STATE.delivery.rainChargeMinOrderValue,
      rainChargeMinProbabilityPercent:
        incoming?.rainCharge?.minProbabilityPercent ?? DEFAULT_STATE.delivery.rainChargeMinProbabilityPercent,
      rainChargeCacheTtlSeconds:
        incoming?.rainCharge?.cacheTtlSeconds ?? DEFAULT_STATE.delivery.rainChargeCacheTtlSeconds,
      rainChargeWarehouseOverrides: normalizeRainChargeWarehouseOverrides(
        incoming?.rainCharge?.warehouseOverrides ??
        DEFAULT_STATE.delivery.rainChargeWarehouseOverrides
      ),

      surgeChargeEnabled: incoming?.surgeCharge?.enabled ?? DEFAULT_STATE.delivery.surgeChargeEnabled,
      surgeChargeActivationMode: normalizeActivationMode(incoming?.surgeCharge?.activationMode ?? incoming?.surgeCharge?.activation_mode),
      surgeChargeAmount: incoming?.surgeCharge?.amount ?? DEFAULT_STATE.delivery.surgeChargeAmount,
      surgeChargeActiveOrderThreshold: incoming?.surgeCharge?.activeOrderThreshold ?? DEFAULT_STATE.delivery.surgeChargeActiveOrderThreshold,
      surgeChargeFreeAboveOrderAmount: incoming?.surgeCharge?.freeAboveOrderAmount ?? DEFAULT_STATE.delivery.surgeChargeFreeAboveOrderAmount,
      surgeChargeWarehouseOverrides: normalizeSurgeChargeWarehouseOverrides(incoming?.surgeCharge?.warehouseOverrides ?? DEFAULT_STATE.delivery.surgeChargeWarehouseOverrides),

      payOnDeliveryEnabled:
        incoming?.payOnDelivery?.enabled ?? DEFAULT_STATE.delivery.payOnDeliveryEnabled,
      payOnDeliveryProvider:
        incoming?.payOnDelivery?.provider ?? DEFAULT_STATE.delivery.payOnDeliveryProvider,
      payOnDeliveryWarehouseOverrides: normalizePayOnDeliveryWarehouseOverrides(
        incoming?.payOnDelivery?.warehouseOverrides ??
        DEFAULT_STATE.delivery.payOnDeliveryWarehouseOverrides
      ),
      payOnDeliveryRestaurantOverrides: normalizePayOnDeliveryRestaurantOverrides(
        incoming?.payOnDelivery?.restaurantOverrides ??
        DEFAULT_STATE.delivery.payOnDeliveryRestaurantOverrides
      ),
      // These settings are managed from Payment Collection V2, not this form.
      // Keep them in form state so saving Delivery / Charges cannot silently
      // replace them with backend defaults.
      payOnDeliveryCollectionFlow:
        incoming?.payOnDelivery?.collectionFlow ??
        incoming?.payOnDelivery?.collection_flow ??
        incoming?.pay_on_delivery?.collectionFlow ??
        incoming?.pay_on_delivery?.collection_flow,
      deliveryOtp:
        incoming?.deliveryOtp ??
        incoming?.delivery_otp ??
        incoming?.customerDeliveryOtp ??
        incoming?.customer_delivery_otp,
    };
  }

  if (presetKey === "scheduler") {
    const jobs = incoming?.jobs || incoming || {};

    const parseEveryCron = (cron, fallbackEvery, fallbackUnit = "minutes") => {
      const value = String(cron || "").trim();

      const everyMinuteMatch = value.match(/^\*\/(\d+)\s+\*\s+\*\s+\*\s+\*$/);
      if (everyMinuteMatch) {
        return {
          every: Math.max(0, Number(everyMinuteMatch[1])),
          unit: "minutes",
        };
      }

      const everyHourMatch = value.match(/^0\s+\*\/(\d+)\s+\*\s+\*\s+\*$/);
      if (everyHourMatch) {
        return {
          every: Math.max(0, Number(everyHourMatch[1])),
          unit: "hours",
        };
      }

      return {
        every: fallbackEvery,
        unit: fallbackUnit,
      };
    };

    const parseTimeCron = (cron, fallback = "09:00") => {
      const value = String(cron || "").trim();
      const match = value.match(/^(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*$/);
      if (!match) return fallback;

      const minute = String(match[1]).padStart(2, "0");
      const hour = String(match[2]).padStart(2, "0");
      return `${hour}:${minute}`;
    };

    const orderAutoCancel = parseEveryCron(
      jobs.orderAutoCancelCron,
      DEFAULT_STATE.scheduler.orderAutoCancelEvery
    );

    const deliveryReassign = parseEveryCron(
      jobs.deliveryReassignCron,
      DEFAULT_STATE.scheduler.deliveryReassignEvery
    );

    const notificationRetry = parseEveryCron(
      jobs.notificationRetryCron,
      DEFAULT_STATE.scheduler.notificationRetryEvery
    );

    const cartHoldCleanup = parseEveryCron(
      jobs.cartHoldCleanupCron,
      DEFAULT_STATE.scheduler.cartHoldCleanupEvery
    );

    return {
      ...DEFAULT_STATE.scheduler,
      orderAutoCancelEvery: orderAutoCancel.every,
      orderAutoCancelUnit: orderAutoCancel.unit,
      deliveryReassignEvery: deliveryReassign.every,
      deliveryReassignUnit: deliveryReassign.unit,
      stockSyncTime: parseTimeCron(
        jobs.stockSyncCron,
        DEFAULT_STATE.scheduler.stockSyncTime
      ),
      stockAlertsTime: parseTimeCron(
        jobs.stockAlertsCron || jobs.stockAlertCron,
        DEFAULT_STATE.scheduler.stockAlertsTime
      ),
      notificationRetryEvery: notificationRetry.every,
      notificationRetryUnit: notificationRetry.unit,
      cartHoldCleanupEvery: cartHoldCleanup.every,
      cartHoldCleanupUnit: cartHoldCleanup.unit,
      dataArchiveTime: parseTimeCron(
        jobs.dataArchiveCron,
        DEFAULT_STATE.scheduler.dataArchiveTime
      ),
    };
  }

  if (presetKey === "warehouseDefaults") {
    return {
      ...DEFAULT_STATE.warehouseDefaults,
      expiryAlertDays:
        incoming?.stock?.expiryAlertDays ??
        DEFAULT_STATE.warehouseDefaults.expiryAlertDays,
      lowStockThreshold:
        incoming?.stock?.lowStockThreshold ??
        DEFAULT_STATE.warehouseDefaults.lowStockThreshold,
      timezone:
        incoming?.operations?.timezone ?? DEFAULT_STATE.warehouseDefaults.timezone,
      openingTime:
        incoming?.operations?.openingTime ??
        DEFAULT_STATE.warehouseDefaults.openingTime,
      closingTime:
        incoming?.operations?.closingTime ??
        DEFAULT_STATE.warehouseDefaults.closingTime,
    };
  }

  if (presetKey === "deliveryIncentives") {
    return {
      ...DEFAULT_STATE.deliveryIncentives,
      enabled: incoming?.enabled ?? DEFAULT_STATE.deliveryIncentives.enabled,
      currency: "INR",
      stackingRule:
        incoming?.stackingRule ?? DEFAULT_STATE.deliveryIncentives.stackingRule,

      dailyEnabled:
        incoming?.daily?.enabled ?? DEFAULT_STATE.deliveryIncentives.dailyEnabled,
      dailyResetTimezone:
        incoming?.daily?.resetTimezone ??
        DEFAULT_STATE.deliveryIncentives.dailyResetTimezone,
      dailySlabs: normalizeIncentiveSlabs(
        incoming?.daily?.slabs ?? DEFAULT_STATE.deliveryIncentives.dailySlabs
      ),
      globalDailySlabs: normalizeIncentiveSlabs(
        incoming?.daily?.slabs ?? DEFAULT_STATE.deliveryIncentives.dailySlabs
      ),

      weeklyEnabled:
        incoming?.weekly?.enabled ?? DEFAULT_STATE.deliveryIncentives.weeklyEnabled,
      weeklyWeekStartsOn:
        incoming?.weekly?.weekStartsOn ??
        DEFAULT_STATE.deliveryIncentives.weeklyWeekStartsOn,
      weeklySlabs: normalizeIncentiveSlabs(
        incoming?.weekly?.slabs ?? DEFAULT_STATE.deliveryIncentives.weeklySlabs
      ),
      globalWeeklySlabs: normalizeIncentiveSlabs(
        incoming?.weekly?.slabs ?? DEFAULT_STATE.deliveryIncentives.weeklySlabs
      ),

      monthlyEnabled:
        incoming?.monthly?.enabled ?? DEFAULT_STATE.deliveryIncentives.monthlyEnabled,
      monthlySlabs: normalizeIncentiveSlabs(
        incoming?.monthly?.slabs ?? DEFAULT_STATE.deliveryIncentives.monthlySlabs
      ),
      globalMonthlySlabs: normalizeIncentiveSlabs(
        incoming?.monthly?.slabs ?? DEFAULT_STATE.deliveryIncentives.monthlySlabs
      ),

      warehouseOverrides: Array.isArray(incoming?.warehouseOverrides)
        ? incoming.warehouseOverrides
        : [],
    };
  }


  if (presetKey === "reportRetention") {
    const incomingTables = incoming?.tables && typeof incoming.tables === "object" ? incoming.tables : {};
    const hasArchiveTableSettings = Object.keys(incomingTables).length > 0;
    const legacyRawDays =
      incoming?.rawRetentionDays ??
      incoming?.raw_retention_days ??
      incoming?.retentionDays ??
      DEFAULT_STATE.reportRetention.tables.driver_availability_audit.retentionDays;

    const tables = {};
    for (const item of ARCHIVE_TABLE_FIELDS) {
      const fallback = DEFAULT_STATE.reportRetention.tables[item.key];
      const stored = incomingTables?.[item.key] || {};
      tables[item.key] = {
        enabled: stored?.enabled ?? fallback.enabled,
        retentionDays:
          stored?.retentionDays ??
          stored?.retention_days ??
          (item.key === "driver_availability_audit" ? legacyRawDays : fallback.retentionDays),
      };
    }

    return {
      ...DEFAULT_STATE.reportRetention,
      enabled: hasArchiveTableSettings
        ? (incoming?.enabled ?? DEFAULT_STATE.reportRetention.enabled)
        : false,
      batchSize:
        incoming?.batchSize ?? incoming?.batch_size ?? DEFAULT_STATE.reportRetention.batchSize,
      maxPartsPerTablePerRun:
        incoming?.maxPartsPerTablePerRun ??
        incoming?.max_parts_per_table_per_run ??
        DEFAULT_STATE.reportRetention.maxPartsPerTablePerRun,
      tables,
    };
  }

  return item || {};
}

function buildPayloadFromState(presetKey, values) {
  if (presetKey === "userAuth") {
    const emailAuthEnabled = !!values.emailAuthEnabled;

    return {
      emailAuthEnabled,
      phoneAuthEnabled: true,
      auth: {
        emailAuthEnabled,
        phoneAuthEnabled: true,
      },
      login: {
        allowEmail: emailAuthEnabled,
        allowPhone: true,
      },
      signup: {
        allowEmail: emailAuthEnabled,
        allowPhone: true,
        otpChannel: emailAuthEnabled ? "email" : "phone",
      },
    };
  }

  if (presetKey === "delivery") {
    return {
      cart: {
        holdMinutes: Math.max(0, Number(values.cartHoldMinutes || 0)),
      },
      cache: {
        serviceabilityTtlSeconds: Math.max(
          0,
          Number(values.serviceabilityTtlSeconds || 0)
        ),
        deliveryPartnerTtlSeconds: Math.max(
          0,
          Number(values.deliveryPartnerTtlSeconds || 0)
        ),
        productAvailabilityTtlSeconds: Math.max(
          0,
          Number(values.productAvailabilityTtlSeconds || 0)
        ),
      },
      lookup: {
        searchCityFallback: !!values.searchCityFallback,
      },
      radius: {
        defaultOrderKm: Math.max(0, Number(values.defaultOrderKm || 0)),
        // Keep legacy shared radius fields aligned with grocery search for older consumers.
        partnerSearchKm: Math.max(0.1, Number(values.groceryInitialSearchKm || 0.1)),
        maxPartnerRadiusKm: Math.max(
          0.1,
          Number(values.groceryMaxSearchKm || 0.1),
          Number(values.foodMaxSearchKm || 0.1)
        ),
        groceryInitialSearchKm: Math.max(0.1, Number(values.groceryInitialSearchKm || 0.1)),
        groceryMaxSearchKm: Math.max(0.1, Number(values.groceryMaxSearchKm || 0.1)),
        foodInitialSearchKm: Math.max(0.1, Number(values.foodInitialSearchKm || 0.1)),
        foodMaxSearchKm: Math.max(0.1, Number(values.foodMaxSearchKm || 0.1)),
      },
      partner: {
        recentSeconds: Math.max(0, Number(values.recentSeconds || 0)),
        activeStatuses: [...DEFAULT_STATE.delivery.activeStatuses],
        approvalStatuses: [...DEFAULT_STATE.delivery.approvalStatuses],
        riderOnlineTtlSeconds: Math.max(
          0,
          Number(values.riderOnlineTtlSeconds || 0)
        ),
      },
      assignment: {
        maxCandidates: Math.max(1, Number(values.maxCandidates || 1)),
        // Legacy backend field. Hidden from UI because current dispatch directly assigns
        // an eligible driver after the order/availability check; there is no accept timer.
        autoApproveSeconds: 20,
        defaultMaxActiveOrders: Math.max(
          1,
          Number(values.defaultMaxActiveOrders || 1)
        ),
        maxFoodOrdersPerPartner: Math.max(
          1,
          Number(values.maxFoodOrdersPerPartner || 1)
        ),
        maxGroceryOrdersPerPartner: Math.max(
          1,
          Number(values.maxGroceryOrdersPerPartner || 1)
        ),
        blockAssignmentWhileFoodPickupPending:
          !!values.blockAssignmentWhileFoodPickupPending,
        foodDriverReservationMinutes: Math.max(
          1,
          Number(values.foodDriverReservationMinutes || 1)
        ),
        dispatchRetry: {
          enabled: !!values.dispatchRetryEnabled,
          repeatSeconds: Math.max(
            60,
            Number(values.dispatchRetryIntervalSeconds || 60)
          ),
          staleAfterSeconds: Math.max(
            10,
            Number(values.dispatchRetryStaleAfterSeconds || 10)
          ),
          maxAgeMinutes: Math.max(
            5,
            Number(values.dispatchRetryMaxAgeMinutes || 5)
          ),
          batchSize: Math.max(
            1,
            Number(values.dispatchRetryBatchSize || 1)
          ),
          maxAttempts: Math.max(
            1,
            Number(values.dispatchRetryMaxAttempts || 1)
          ),
        },
      },
      groceryMinimumOrderValue: Math.max(
        0,
        Number(values.groceryMinimumOrderValue || 0)
      ),
      grocerySmallCartCharge: {
        enabled: Boolean(values.grocerySmallCartChargeEnabled),
        amount: Math.max(0, Number(values.grocerySmallCartChargeAmount || 0)),
      },
      deliveryFee: {
        enabled: !!values.deliveryFeeEnabled,
        currency: "INR",
        baseKm: Math.max(0, Number(values.deliveryFeeBaseKm || 0)),
        baseFee: Math.max(0, Number(values.deliveryFeeBaseFee || 0)),
        perKmCharge: Math.max(0, Number(values.deliveryFeePerKmCharge || 0)),
        minimumFee: Math.max(0, Number(values.deliveryFeeMinimumFee || 0)),
        maxFee: Math.max(0, Number(values.deliveryFeeMaxFee || 0)),
        freeDeliveryAbove: Math.max(
          0,
          Number(values.deliveryFeeFreeDeliveryAbove || 0)
        ),
        roundTo: Math.max(0, Number(values.deliveryFeeRoundTo || 1)),
        warehouseOverrides: normalizeDeliveryFeeWarehouseOverrides(
          values.deliveryFeeWarehouseOverrides
        ),
      },
      driverEarningRule: {
        enabled: !!values.driverEarningEnabled,
        currency: "INR",
        mode: normalizeDriverEarningMode(values.driverEarningMode),
        fixedAmount: Math.max(0, Number(values.driverEarningFixedAmount || 0)),
        baseKm: Math.max(0, Number(values.driverEarningBaseKm || 0)),
        baseFee: Math.max(0, Number(values.driverEarningBaseFee || 0)),
        perKmCharge: Math.max(0, Number(values.driverEarningPerKmCharge || 0)),
        minimumFee: Math.max(0, Number(values.driverEarningMinimumFee || 0)),
        maxFee: Math.max(0, Number(values.driverEarningMaxFee || 0)),
        roundTo: Math.max(0, Number(values.driverEarningRoundTo || 1)),
        warehouseOverrides: normalizeDriverEarningWarehouseOverrides(
          values.driverEarningWarehouseOverrides
        ),
      },
      handlingCharge: {
        enabled: !!values.handlingChargeEnabled,
        currency: "INR",
        freeUpToKg: Math.max(0, Number(values.handlingChargeFreeUpToKg || 0)),
        baseCharge: Math.max(0, Number(values.handlingChargeBaseCharge || 0)),
        perKgCharge: Math.max(0, Number(values.handlingChargePerKgCharge || 0)),
        minimumCharge: Math.max(0, Number(values.handlingChargeMinimumCharge || 0)),
        maxCharge: Math.max(0, Number(values.handlingChargeMaxCharge || 0)),
        roundWeightToKg: Math.max(0, Number(values.handlingChargeRoundWeightToKg || 0)),
        roundTo: Math.max(0, Number(values.handlingChargeRoundTo || 1)),
        defaultItemWeightKg: Math.max(0, Number(values.handlingChargeDefaultItemWeightKg || 0)),
        warehouseOverrides: normalizeHandlingChargeWarehouseOverrides(values.handlingChargeWarehouseOverrides),
      },
      rainCharge: {
        enabled: !!values.rainChargeEnabled,
        activationMode: normalizeActivationMode(values.rainChargeActivationMode),
        currency: "INR",
        amount: Math.max(0, Number(values.rainChargeAmount || 0)),
        minOrderValue: Math.max(0, Number(values.rainChargeMinOrderValue || 0)),
        minProbabilityPercent: Math.min(100, Math.max(0, Number(values.rainChargeMinProbabilityPercent || 50))),
        cacheTtlSeconds: Math.max(30, Number(values.rainChargeCacheTtlSeconds || 300)),
        requireRainType: true,
        applyWhenWeatherApiFails: false,
        warehouseOverrides: normalizeRainChargeWarehouseOverrides(values.rainChargeWarehouseOverrides),
      },
      surgeCharge: {
        enabled: !!values.surgeChargeEnabled,
        currency: "INR",
        activationMode: normalizeActivationMode(values.surgeChargeActivationMode),
        amount: Math.max(0, Number(values.surgeChargeAmount || 0)),
        activeOrderThreshold: Math.max(1, Math.trunc(Number(values.surgeChargeActiveOrderThreshold || 20))),
        freeAboveOrderAmount: Math.max(0, Number(values.surgeChargeFreeAboveOrderAmount || 0)),
        warehouseOverrides: normalizeSurgeChargeWarehouseOverrides(values.surgeChargeWarehouseOverrides),
      },
      payOnDelivery: {
        enabled: !!values.payOnDeliveryEnabled,
        provider: String(values.payOnDeliveryProvider || "RAZORPAY").trim().toUpperCase() || "RAZORPAY",
        ...(values.payOnDeliveryCollectionFlow && typeof values.payOnDeliveryCollectionFlow === "object"
          ? { collectionFlow: values.payOnDeliveryCollectionFlow }
          : {}),
        warehouseOverrides: normalizePayOnDeliveryWarehouseOverrides(values.payOnDeliveryWarehouseOverrides),
        restaurantOverrides: normalizePayOnDeliveryRestaurantOverrides(values.payOnDeliveryRestaurantOverrides),
      },
      ...(values.deliveryOtp && typeof values.deliveryOtp === "object"
        ? { deliveryOtp: values.deliveryOtp }
        : {}),
    };
  }

  if (presetKey === "scheduler") {
    const buildEveryCron = (every, unit) => {
      const n = Math.max(0, Number(every || 0));
      if (!n) return "";

      if (unit === "hours") return `0 */${n} * * *`;
      return `*/${n} * * * *`;
    };

    const buildTimeCron = (time) => {
      const [hour = "09", minute = "00"] = String(time || "09:00").split(":");
      return `${Number(minute)} ${Number(hour)} * * *`;
    };

    return {
      stockSyncCron: buildTimeCron(values.stockSyncTime),
      stockAlertsCron: buildTimeCron(values.stockAlertsTime),
      orderAutoCancelCron: buildEveryCron(
        values.orderAutoCancelEvery,
        values.orderAutoCancelUnit
      ),
      deliveryReassignCron: buildEveryCron(
        values.deliveryReassignEvery,
        values.deliveryReassignUnit
      ),
      notificationRetryCron: buildEveryCron(
        values.notificationRetryEvery,
        values.notificationRetryUnit
      ),
      cartHoldCleanupCron: buildEveryCron(
        values.cartHoldCleanupEvery,
        values.cartHoldCleanupUnit
      ),
      dataArchiveCron: buildTimeCron(values.dataArchiveTime),
    };
  }

  if (presetKey === "warehouseDefaults") {
    return {
      stock: {
        expiryAlertDays: Math.max(0, Number(values.expiryAlertDays || 0)),
        lowStockThreshold: Math.max(0, Number(values.lowStockThreshold || 0)),
      },
      operations: {
        timezone: values.timezone || "",
        openingTime: values.openingTime || "",
        closingTime: values.closingTime || "",
      },
    };
  }

  if (presetKey === "deliveryIncentives") {
    return {
      enabled: !!values.enabled,
      currency: "INR",
      stackingRule: values.stackingRule || "top_up",

      daily: {
        enabled: !!values.dailyEnabled,
        resetTimezone: values.dailyResetTimezone || "Asia/Kolkata",
        slabs: normalizeIncentiveSlabs(
          values.selectedState ? values.globalDailySlabs : values.dailySlabs
        ),
      },

      weekly: {
        enabled: !!values.weeklyEnabled,
        weekStartsOn: values.weeklyWeekStartsOn || "MONDAY",
        slabs: normalizeIncentiveSlabs(
          values.selectedState ? values.globalWeeklySlabs : values.weeklySlabs
        ),
      },

      monthly: {
        enabled: !!values.monthlyEnabled,
        slabs: normalizeIncentiveSlabs(
          values.selectedState ? values.globalMonthlySlabs : values.monthlySlabs
        ),
      },

      warehouseOverrides: Array.isArray(values.warehouseOverrides)
        ? values.warehouseOverrides
        : [],
    };
  }


  if (presetKey === "reportRetention") {
    const tables = {};
    for (const item of ARCHIVE_TABLE_FIELDS) {
      const current = values?.tables?.[item.key] || DEFAULT_STATE.reportRetention.tables[item.key];
      tables[item.key] = {
        enabled: !!current?.enabled,
        retentionDays: Math.max(1, Number(current?.retentionDays || 1)),
      };
    }

    return {
      enabled: !!values.enabled,
      batchSize: Math.min(50000, Math.max(100, Number(values.batchSize || 10000))),
      maxPartsPerTablePerRun: Math.min(1000, Math.max(1, Number(values.maxPartsPerTablePerRun || 10))),
      tables,
    };
  }

  return values;
}

export default function SettingsPage() {
  const [activeKey, setActiveKey] = useState(PRESETS[0].key);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formMap, setFormMap] = useState(DEFAULT_STATE);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [presetsCollapsed, setPresetsCollapsed] = useState(false);

  const [popupState, setPopupState] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const activePreset = useMemo(
    () => PRESETS.find((item) => item.key === activeKey) || PRESETS[0],
    [activeKey]
  );

  const activeValues =
    formMap[activeKey] ||
    DEFAULT_STATE[activeKey] ||
    (activeKey === "charges" ? DEFAULT_STATE.delivery : {});

  useEffect(() => {
    let mounted = true;

    async function loadWarehouseOptions() {
      try {
        const res = await settingsService.getWarehouseFilterOptions({
          status: "active",
        });

        const rows =
          res?.warehouses ||
          res?.data?.warehouses ||
          res?.items ||
          res?.data?.items ||
          [];

        if (!mounted) return;

        setWarehouseOptions(
          rows.map((item) => ({
            id: item.id || item._id || item.warehouseId,
            name: item.name || item.warehouseName || item.code || item.id,
            code: item.code || "",
            city: item.city || "",
            state: item.state || "",
          }))
        );
      } catch (error) {
        console.error("Failed to load warehouse filter options", error);
      }
    }

    async function loadRestaurantOptions() {
      try {
        const res = await restaurantAdminService.listRestaurants({ page: 1, limit: 500 });
        const rows = restaurantRowsOf(res);

        if (!mounted) return;

        setRestaurantOptions(
          rows.map((item) => ({
            id: item.id || item._id || item.restaurantId || item.restaurant_id,
            name: item.name || item.restaurantName || item.restaurant_name || item.businessName || item.id,
            city: item.city || item.address?.city || item.location?.city || "",
            status: item.status || "",
          })).filter((item) => item.id)
        );
      } catch (error) {
        console.error("Failed to load restaurant options", error);
      }
    }

    loadWarehouseOptions();
    loadRestaurantOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const updateSlabList = (listKey, index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((item, itemIndex) =>
        itemIndex === index
          ? {
            ...item,
            [field]: parseNumberField(value),
          }
          : item
      ),
    }));
  };

  const addSlabRow = (listKey) => {
    setActiveValues((prev) => ({
      ...prev,
      [listKey]: addEmptySlab(prev[listKey]),
    }));
  };

  const removeSlabRow = (listKey, index) => {
    setActiveValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateDeliveryFeeWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      deliveryFeeWarehouseOverrides: (prev.deliveryFeeWarehouseOverrides || []).map(
        (item, itemIndex) => {
          if (itemIndex !== index) return item;

          if (field === "warehouseId") {
            const selectedWarehouse = warehouseOptions.find(
              (warehouse) => String(warehouse.id) === String(value)
            );

            return {
              ...item,
              warehouseId: value,
              warehouseName: selectedWarehouse?.name || item.warehouseName || "",
            };
          }

          if (field === "enabled") {
            return {
              ...item,
              enabled: Boolean(value),
            };
          }

          return {
            ...item,
            [field]: parseNumberField(value),
          };
        }
      ),
    }));
  };

  const addDeliveryFeeWarehouseOverride = () => {
    setActiveValues((prev) => ({
      ...prev,
      deliveryFeeWarehouseOverrides: addEmptyDeliveryFeeWarehouseOverride(
        prev.deliveryFeeWarehouseOverrides
      ),
    }));
  };

  const removeDeliveryFeeWarehouseOverride = (index) => {
    setActiveValues((prev) => ({
      ...prev,
      deliveryFeeWarehouseOverrides: (prev.deliveryFeeWarehouseOverrides || []).filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const updateDriverEarningWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      driverEarningWarehouseOverrides: (prev.driverEarningWarehouseOverrides || []).map(
        (item, itemIndex) => {
          if (itemIndex !== index) return item;

          if (field === "warehouseId") {
            const selectedWarehouse = warehouseOptions.find(
              (warehouse) => String(warehouse.id) === String(value)
            );

            return {
              ...item,
              warehouseId: value,
              warehouseName: selectedWarehouse?.name || item.warehouseName || "",
            };
          }

          if (field === "enabled") return { ...item, enabled: Boolean(value) };
          if (field === "mode") return { ...item, mode: normalizeDriverEarningMode(value) };
          return { ...item, [field]: parseNumberField(value) };
        }
      ),
    }));
  };

  const addDriverEarningWarehouseOverride = () => {
    setActiveValues((prev) => ({
      ...prev,
      driverEarningWarehouseOverrides: addEmptyDriverEarningWarehouseOverride(
        prev.driverEarningWarehouseOverrides
      ),
    }));
  };

  const removeDriverEarningWarehouseOverride = (index) => {
    setActiveValues((prev) => ({
      ...prev,
      driverEarningWarehouseOverrides: (prev.driverEarningWarehouseOverrides || []).filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const updateHandlingChargeWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({ ...prev, handlingChargeWarehouseOverrides: (prev.handlingChargeWarehouseOverrides || []).map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (field === "warehouseId") {
        const selectedWarehouse = warehouseOptions.find((warehouse) => String(warehouse.id) === String(value));
        return { ...item, warehouseId: value, warehouseName: selectedWarehouse?.name || item.warehouseName || "" };
      }
      if (field === "enabled") return { ...item, enabled: Boolean(value) };
      return { ...item, [field]: value };
    }) }));
  };
  const addHandlingChargeWarehouseOverride = () => setActiveValues((prev) => ({ ...prev, handlingChargeWarehouseOverrides: addEmptyHandlingChargeWarehouseOverride(prev.handlingChargeWarehouseOverrides) }));
  const removeHandlingChargeWarehouseOverride = (index) => setActiveValues((prev) => ({ ...prev, handlingChargeWarehouseOverrides: (prev.handlingChargeWarehouseOverrides || []).filter((_, i) => i !== index) }));

  const updateSurgeChargeWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({ ...prev, surgeChargeWarehouseOverrides: (prev.surgeChargeWarehouseOverrides || []).map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (field === "warehouseId") {
        const selectedWarehouse = warehouseOptions.find((warehouse) => String(warehouse.id) === String(value));
        return { ...item, warehouseId: value, warehouseName: selectedWarehouse?.name || item.warehouseName || "" };
      }
      if (field === "enabled") return { ...item, enabled: Boolean(value) };
      return { ...item, [field]: value };
    }) }));
  };
  const addSurgeChargeWarehouseOverride = () => setActiveValues((prev) => ({ ...prev, surgeChargeWarehouseOverrides: addEmptySurgeChargeWarehouseOverride(prev.surgeChargeWarehouseOverrides) }));
  const removeSurgeChargeWarehouseOverride = (index) => setActiveValues((prev) => ({ ...prev, surgeChargeWarehouseOverrides: (prev.surgeChargeWarehouseOverrides || []).filter((_, i) => i !== index) }));

  const updateRainChargeWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      rainChargeWarehouseOverrides: (prev.rainChargeWarehouseOverrides || []).map(
        (item, itemIndex) => {
          if (itemIndex !== index) return item;
          if (field === "warehouseId") {
            const selectedWarehouse = warehouseOptions.find(
              (warehouse) => String(warehouse.id) === String(value)
            );
            return {
              ...item,
              warehouseId: value,
              warehouseName: selectedWarehouse?.name || item.warehouseName || "",
            };
          }
          if (field === "enabled") return { ...item, enabled: Boolean(value) };
          if (field === "currency") return { ...item, currency: "INR" };
          return { ...item, [field]: parseNumberField(value) };
        }
      ),
    }));
  };

  const addRainChargeWarehouseOverride = () => {
    setActiveValues((prev) => ({
      ...prev,
      rainChargeWarehouseOverrides: addEmptyRainChargeWarehouseOverride(prev.rainChargeWarehouseOverrides),
    }));
  };

  const removeRainChargeWarehouseOverride = (index) => {
    setActiveValues((prev) => ({
      ...prev,
      rainChargeWarehouseOverrides: (prev.rainChargeWarehouseOverrides || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updatePayOnDeliveryWarehouseOverride = (index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryWarehouseOverrides: (prev.payOnDeliveryWarehouseOverrides || []).map(
        (item, itemIndex) => {
          if (itemIndex !== index) return item;
          if (field === "warehouseId") {
            const selectedWarehouse = warehouseOptions.find(
              (warehouse) => String(warehouse.id) === String(value)
            );
            return {
              ...item,
              warehouseId: value,
              warehouseName: selectedWarehouse?.name || item.warehouseName || "",
            };
          }
          if (field === "enabled") return { ...item, enabled: Boolean(value) };
          return { ...item, [field]: value };
        }
      ),
    }));
  };

  const addPayOnDeliveryWarehouseOverride = () => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryWarehouseOverrides: addEmptyPayOnDeliveryWarehouseOverride(prev.payOnDeliveryWarehouseOverrides),
    }));
  };

  const removePayOnDeliveryWarehouseOverride = (index) => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryWarehouseOverrides: (prev.payOnDeliveryWarehouseOverrides || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updatePayOnDeliveryRestaurantOverride = (index, field, value) => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryRestaurantOverrides: (prev.payOnDeliveryRestaurantOverrides || []).map(
        (item, itemIndex) => {
          if (itemIndex !== index) return item;
          if (field === "restaurantId") {
            const selectedRestaurant = restaurantOptions.find(
              (restaurant) => String(restaurant.id) === String(value)
            );
            return {
              ...item,
              restaurantId: value,
              restaurantName: selectedRestaurant?.name || item.restaurantName || "",
            };
          }
          if (field === "enabled") return { ...item, enabled: Boolean(value) };
          return { ...item, [field]: value };
        }
      ),
    }));
  };

  const addPayOnDeliveryRestaurantOverride = () => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryRestaurantOverrides: addEmptyPayOnDeliveryRestaurantOverride(prev.payOnDeliveryRestaurantOverrides),
    }));
  };

  const removePayOnDeliveryRestaurantOverride = (index) => {
    setActiveValues((prev) => ({
      ...prev,
      payOnDeliveryRestaurantOverrides: (prev.payOnDeliveryRestaurantOverrides || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const renderSlabEditor = (listKey, title, helper, addLabel) => {
    const rows = Array.isArray(activeValues[listKey]) ? activeValues[listKey] : [];

    return (
      <SectionCard icon={Coins} title={title} description={helper}>
        <div className="space-y-4">
          {rows.length ? (
            <div className="space-y-3">
              {rows.map((row, index) => (
                <div
                  key={`${listKey}-${index}`}
                  className="grid gap-3 rounded-[18px] border p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end"
                  style={{ borderColor: BORDER }}
                >
                  <Field label="Min Deliveries">
                    <TextInput
                      type="number"
                      min={0}
                      value={row.minDeliveries}
                      onChange={(e) =>
                        updateSlabList(listKey, index, "minDeliveries", e.target.value)
                      }
                      placeholder="10"
                    />
                  </Field>

                  <Field label="Amount">
                    <TextInput
                      type="number"
                      min={0}
                      value={row.amount}
                      onChange={(e) =>
                        updateSlabList(listKey, index, "amount", e.target.value)
                      }
                      placeholder="100"
                    />
                  </Field>

                  <button
                    type="button"
                    onClick={() => removeSlabRow(listKey, index)}
                    className="inline-flex h-[44px] items-center justify-center rounded-[14px] border px-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                    style={{ borderColor: BORDER }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor: BORDER }}
            >
              No slabs added yet. Add the first slab to start controlling incentives.
            </div>
          )}

          <button
            type="button"
            onClick={() => addSlabRow(listKey)}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor: BORDER }}
          >
            <Plus size={16} />
            {addLabel}
          </button>
        </div>
      </SectionCard>
    );
  };

  const setActiveValues = (updater) => {
  setIsDirty(true);

  setFormMap((prev) => {
      const prevValues =
        prev[activeKey] ||
        DEFAULT_STATE[activeKey] ||
        (activeKey === "charges" ? DEFAULT_STATE.delivery : {});
      const nextValues =
        typeof updater === "function" ? updater(prevValues) : updater;

      return {
        ...prev,
        [activeKey]: nextValues,
      };
    });
  };

  const loadPreset = async (presetKey) => {
    const storageKey = getStoragePresetKey(presetKey);
    try {
      setLoading(true);
      const res = await settingsService.getSetting(storageKey);
      const item = normalizeSettingResponse(res);
      const normalized = normalizeIncomingPreset(storageKey, item);

      setFormMap((prev) => ({
        ...prev,
        [presetKey]: normalized,
      }));
      setIsDirty(false);
    } catch (error) {
      console.error(`Failed to load ${presetKey} preset`, error);
      setFormMap((prev) => ({
        ...prev,
        [presetKey]: DEFAULT_STATE[storageKey] || {},
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreset(activeKey);
  }, [activeKey]);

  const handleRefresh = async () => {
    await loadPreset(activeKey);
  };

  const readPresetValues = async (presetKey) => {
    const storageKey = getStoragePresetKey(presetKey);
    const res = await settingsService.getSetting(storageKey);
    const item = normalizeSettingResponse(res);
    return normalizeIncomingPreset(storageKey, item);
  };

  const persistUserAuthSetting = async (settingValue) => {
    const storagePayload = makeSettingStoragePayload("userAuth", settingValue);
    const expectedEmailAuthEnabled = !!settingValue.emailAuthEnabled;

    const attempts = [
      () => settingsService.upsertPreset("userAuth", storagePayload),
      () => settingsService.updateSetting("userAuth", storagePayload),
      () => settingsService.createSetting(storagePayload),
      () => settingsService.upsertPreset("userAuth", settingValue),
    ];

    let lastError = null;

    for (const attempt of attempts) {
      try {
        await attempt();

        const savedValues = await readPresetValues("userAuth");
        if (savedValues.emailAuthEnabled === expectedEmailAuthEnabled) {
          return savedValues;
        }

        lastError = new Error(
          "Settings API accepted the request but did not persist userAuth.emailAuthEnabled."
        );
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("Failed to save user auth setting.");
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const finalValues =
        activeKey === "deliveryIncentives"
          ? {
            ...activeValues,
            warehouseOverrides: upsertBulkIncentiveOverrides(
              activeValues,
              warehouseOptions
            ),
          }
          : activeValues;

      if (activeKey === "delivery") {
        validateDeliverySettings(finalValues);
      }

      const storageKey = getStoragePresetKey(activeKey);
      const payload = buildPayloadFromState(storageKey, finalValues);
      let savedValues = null;

      if (activeKey === "userAuth") {
        savedValues = await persistUserAuthSetting(payload);
      } else {
        await settingsService.upsertPreset(storageKey, payload);
        savedValues = await readPresetValues(activeKey);

        if (activeKey === "delivery" || activeKey === "charges") {
          assertPayOnDeliveryPersisted(finalValues, savedValues);
        }
      }

      setFormMap((prev) => ({
        ...prev,
        [activeKey]: savedValues || finalValues,
      }));
      setIsDirty(false);

      setPopupState({
        open: true,
        type: "success",
        title: activeKey === "charges" ? "Charges & Pricing Updated" : "Settings Saved",
        message:
          activeKey === "charges"
            ? "Charges & Pricing updated successfully."
            : `${activePreset.title} has been saved successfully.`,
      });
    } catch (error) {
      console.error(`Failed to save ${activeKey} preset`, error);
      setPopupState({
        open: true,
        type: "error",
        title: "Save Failed",
        message:
          activeKey === "userAuth"
            ? error?.message ||
            "Settings backend did not persist User Auth Settings. Update the service that owns /api/admin/settings."
            : error?.message || "Failed to save preset.",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderDelivery = () => {
    return (
      <div className="space-y-8">
        <section>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-500">Operations</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">Delivery coverage and driver matching</h3>
              <p className="mt-1 text-sm text-slate-500">Control cache timing, service radius, driver freshness and assignment capacity.</p>
            </div>
          </div>

          <NormalDeliverySettings
            activeValues={activeValues}
            setActiveValues={setActiveValues}
            Field={Field}
            TextInput={TextInput}
            Toggle={Toggle}
            SectionCard={SectionCard}
            parseNumberField={parseNumberField}
            lockCoreDeliveryFields={LOCK_CORE_DELIVERY_FIELDS}
          />
        </section>

        <section className="border-t border-slate-200 pt-8">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-500">Payments & payouts</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">Payment collection controls</h3>
              <p className="mt-1 text-sm text-slate-500">Customer charge rules, restaurant-wise POD and driver earning rules are managed under Charges & Pricing.</p>
            </div>
            <span className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600">
              Currency: INR
            </span>
          </div>

          <PaymentDeliverySettings
            mode="delivery"
            activeValues={activeValues}
            setActiveValues={setActiveValues}
            warehouseOptions={warehouseOptions}
            restaurantOptions={restaurantOptions}
            Field={Field}
            TextInput={TextInput}
            SelectInput={SelectInput}
            Toggle={Toggle}
            SectionCard={SectionCard}
            parseNumberField={parseNumberField}
            updateDeliveryFeeWarehouseOverride={updateDeliveryFeeWarehouseOverride}
            addDeliveryFeeWarehouseOverride={addDeliveryFeeWarehouseOverride}
            removeDeliveryFeeWarehouseOverride={removeDeliveryFeeWarehouseOverride}
            updateDriverEarningWarehouseOverride={updateDriverEarningWarehouseOverride}
            addDriverEarningWarehouseOverride={addDriverEarningWarehouseOverride}
            removeDriverEarningWarehouseOverride={removeDriverEarningWarehouseOverride}
            updateHandlingChargeWarehouseOverride={updateHandlingChargeWarehouseOverride}
            addHandlingChargeWarehouseOverride={addHandlingChargeWarehouseOverride}
            removeHandlingChargeWarehouseOverride={removeHandlingChargeWarehouseOverride}
            updateRainChargeWarehouseOverride={updateRainChargeWarehouseOverride}
            addRainChargeWarehouseOverride={addRainChargeWarehouseOverride}
            removeRainChargeWarehouseOverride={removeRainChargeWarehouseOverride}
            updateSurgeChargeWarehouseOverride={updateSurgeChargeWarehouseOverride}
            addSurgeChargeWarehouseOverride={addSurgeChargeWarehouseOverride}
            removeSurgeChargeWarehouseOverride={removeSurgeChargeWarehouseOverride}
            updatePayOnDeliveryWarehouseOverride={updatePayOnDeliveryWarehouseOverride}
            addPayOnDeliveryWarehouseOverride={addPayOnDeliveryWarehouseOverride}
            removePayOnDeliveryWarehouseOverride={removePayOnDeliveryWarehouseOverride}
            updatePayOnDeliveryRestaurantOverride={updatePayOnDeliveryRestaurantOverride}
            addPayOnDeliveryRestaurantOverride={addPayOnDeliveryRestaurantOverride}
            removePayOnDeliveryRestaurantOverride={removePayOnDeliveryRestaurantOverride}
            borderColor={BORDER}
          />
        </section>
      </div>
    );
  };

  const renderCharges = () => {
    return (
      <div className="space-y-8">
        <section>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-500">Customer pricing</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">Charges & Pricing</h3>
              <p className="mt-1 text-sm text-slate-500">Manage customer charges, restaurant-wise Pay on Delivery and driver earning rules in one place.</p>
            </div>
            <span className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600">
              Currency: INR
            </span>
          </div>

          <PaymentDeliverySettings
            mode="charges"
            activeValues={activeValues}
            setActiveValues={setActiveValues}
            warehouseOptions={warehouseOptions}
            restaurantOptions={restaurantOptions}
            Field={Field}
            TextInput={TextInput}
            SelectInput={SelectInput}
            Toggle={Toggle}
            SectionCard={SectionCard}
            parseNumberField={parseNumberField}
            updateDeliveryFeeWarehouseOverride={updateDeliveryFeeWarehouseOverride}
            addDeliveryFeeWarehouseOverride={addDeliveryFeeWarehouseOverride}
            removeDeliveryFeeWarehouseOverride={removeDeliveryFeeWarehouseOverride}
            updateDriverEarningWarehouseOverride={updateDriverEarningWarehouseOverride}
            addDriverEarningWarehouseOverride={addDriverEarningWarehouseOverride}
            removeDriverEarningWarehouseOverride={removeDriverEarningWarehouseOverride}
            updateHandlingChargeWarehouseOverride={updateHandlingChargeWarehouseOverride}
            addHandlingChargeWarehouseOverride={addHandlingChargeWarehouseOverride}
            removeHandlingChargeWarehouseOverride={removeHandlingChargeWarehouseOverride}
            updateRainChargeWarehouseOverride={updateRainChargeWarehouseOverride}
            addRainChargeWarehouseOverride={addRainChargeWarehouseOverride}
            removeRainChargeWarehouseOverride={removeRainChargeWarehouseOverride}
            updateSurgeChargeWarehouseOverride={updateSurgeChargeWarehouseOverride}
            addSurgeChargeWarehouseOverride={addSurgeChargeWarehouseOverride}
            removeSurgeChargeWarehouseOverride={removeSurgeChargeWarehouseOverride}
            updatePayOnDeliveryWarehouseOverride={updatePayOnDeliveryWarehouseOverride}
            addPayOnDeliveryWarehouseOverride={addPayOnDeliveryWarehouseOverride}
            removePayOnDeliveryWarehouseOverride={removePayOnDeliveryWarehouseOverride}
            updatePayOnDeliveryRestaurantOverride={updatePayOnDeliveryRestaurantOverride}
            addPayOnDeliveryRestaurantOverride={addPayOnDeliveryRestaurantOverride}
            removePayOnDeliveryRestaurantOverride={removePayOnDeliveryRestaurantOverride}
            borderColor={BORDER}
          />
        </section>
      </div>
    );
  };

  const renderScheduler = () => {
    return (
      <div className="space-y-5">
        <SectionCard
          icon={Clock3}
          title="Scheduler Jobs"
          description="Admin-friendly scheduler configuration"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Order Auto Cancel">
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.orderAutoCancelEvery}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      orderAutoCancelEvery: parseNumberField(e.target.value),
                    }))
                  }
                  placeholder="10"
                />
                <SelectInput
                  value={activeValues.orderAutoCancelUnit}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      orderAutoCancelUnit: e.target.value,
                    }))
                  }
                  options={[
                    { label: "Minutes", value: "minutes" },
                    { label: "Hours", value: "hours" },
                  ]}
                />
              </div>
            </Field>

            <Field label="Delivery Reassign">
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.deliveryReassignEvery}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      deliveryReassignEvery: parseNumberField(e.target.value),
                    }))
                  }
                  placeholder="5"
                />
                <SelectInput
                  value={activeValues.deliveryReassignUnit}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      deliveryReassignUnit: e.target.value,
                    }))
                  }
                  options={[
                    { label: "Minutes", value: "minutes" },
                    { label: "Hours", value: "hours" },
                  ]}
                />
              </div>
            </Field>

            <Field label="Stock Sync Time">
              <TextInput
                type="time"
                value={activeValues.stockSyncTime}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    stockSyncTime: e.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Stock Alert Time">
              <TextInput
                type="time"
                value={activeValues.stockAlertsTime}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    stockAlertsTime: e.target.value,
                  }))
                }
              />
            </Field>

            <Field
              label="Data Archive Time"
              info="Daily time when old dump/audit rows are archived to MinIO, verified, and only then removed from DB. Uses Warehouse Defaults timezone."
            >
              <TextInput
                type="time"
                value={activeValues.dataArchiveTime}
                onChange={(e) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    dataArchiveTime: e.target.value,
                  }))
                }
              />
            </Field>

            <Field label="Notification Retry">
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.notificationRetryEvery}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      notificationRetryEvery: parseNumberField(e.target.value),
                    }))
                  }
                  placeholder="15"
                />
                <SelectInput
                  value={activeValues.notificationRetryUnit}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      notificationRetryUnit: e.target.value,
                    }))
                  }
                  options={[
                    { label: "Minutes", value: "minutes" },
                    { label: "Hours", value: "hours" },
                  ]}
                />
              </div>
            </Field>

            <Field label="Cart Hold Cleanup">
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.cartHoldCleanupEvery}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      cartHoldCleanupEvery: parseNumberField(e.target.value),
                    }))
                  }
                  placeholder="1"
                />
                <SelectInput
                  value={activeValues.cartHoldCleanupUnit}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      cartHoldCleanupUnit: e.target.value,
                    }))
                  }
                  options={[
                    { label: "Minutes", value: "minutes" },
                    { label: "Hours", value: "hours" },
                  ]}
                />
              </div>
            </Field>
          </div>
        </SectionCard>
      </div>
    );
  };

  const renderWarehouseDefaults = () => {
    return (
      <div className="space-y-5">
        <div className="grid gap-5 xl:grid-cols-2">
          <SectionCard
            icon={Warehouse}
            title="Warehouse Operations"
            description="Default opening, closing and timezone values"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Opening Time">
                <TextInput
                  type="time"
                  value={activeValues.openingTime}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      openingTime: e.target.value,
                    }))
                  }
                />
              </Field>

              <Field label="Closing Time">
                <TextInput
                  type="time"
                  value={activeValues.closingTime}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      closingTime: e.target.value,
                    }))
                  }
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Timezone">
                  <TextInput
                    value={activeValues.timezone}
                    onChange={(e) =>
                      setActiveValues((prev) => ({
                        ...prev,
                        timezone: e.target.value,
                      }))
                    }
                    placeholder="Asia/Kolkata"
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={PackageCheck}
            title="Stock Alerts"
            description="Warehouse level stock alert thresholds"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Expiry Alert Days">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.expiryAlertDays}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      expiryAlertDays: parseNumberField(e.target.value),
                    }))
                  }
                />
              </Field>

              <Field label="Low Stock Threshold">
                <TextInput
                  type="number"
                  min={0}
                  value={activeValues.lowStockThreshold}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      lowStockThreshold: parseNumberField(e.target.value),
                    }))
                  }
                />
              </Field>
            </div>
          </SectionCard>
        </div>
      </div>
    );
  };

  const renderDeliveryIncentives = () => {
    const states = Array.from(
      new Set(warehouseOptions.map((w) => w.state).filter(Boolean))
    ).sort();

    const filteredWarehouses = warehouseOptions.filter(
      (w) => !activeValues.selectedState || w.state === activeValues.selectedState
    );

    const visibleOverrides = (activeValues.warehouseOverrides || []).filter(
      (item) =>
        !activeValues.selectedState ||
        String(item.state || "") === String(activeValues.selectedState)
    );
    return (
      <div className="space-y-5">
        <SectionCard
          icon={Warehouse}
          title="Warehouse Filter"
          description="Select state first, then choose warehouse for incentive override"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="State">
              <SelectInput
                value={activeValues.selectedState}
                onChange={(e) => {
                  const state = e.target.value;

                  setActiveValues((prev) => ({
                    ...prev,
                    selectedState: state,
                    selectedWarehouseId: "",
                    selectedWarehouseIds: [],

                    dailySlabs: state ? [] : (prev.globalDailySlabs || []),
                    weeklySlabs: state ? [] : (prev.globalWeeklySlabs || []),
                    monthlySlabs: state ? [] : (prev.globalMonthlySlabs || []),
                  }));
                }}
                options={[
                  { label: "Global Default", value: "" },
                  ...states.map((state) => ({
                    label: state,
                    value: state,
                  })),
                ]}
              />
            </Field>
            
            {activeValues.selectedState ? (
              <div className="md:col-span-2">
                <Field label="Apply Same Incentive To Warehouses">
                  <WarehouseCheckboxList
                    value={activeValues.selectedWarehouseIds || []}
                    onChange={(ids) =>
                      setActiveValues((prev) => ({
                        ...prev,
                        selectedWarehouseIds: ids,
                      }))
                    }
                    options={filteredWarehouses.map((w) => ({
                      label: `${w.name}${w.city ? ` - ${w.city}` : ""}`,
                      value: String(w.id),
                    }))}
                  />
                </Field>

                <p className="mt-2 text-xs text-slate-500">
                  Select one or multiple warehouses. Same slabs will apply to selected warehouses.
                </p>
              </div>
            ) : null}
          </div>
        </SectionCard>
        <div className="grid gap-5 xl:grid-cols-2">
          <SectionCard
            icon={Coins}
            title="Incentive Controls"
            description="Turn incentives on or off and control payout stacking"
          >
            <div className="space-y-4">
              <Toggle
                label="Enable Incentives"
                checked={!!activeValues.enabled}
                onChange={(next) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    enabled: next,
                  }))
                }
              />

              <div>
                <Field label="Stacking Rule">
                  <SelectInput
                    value={activeValues.stackingRule}
                    onChange={(e) =>
                      setActiveValues((prev) => ({
                        ...prev,
                        stackingRule: e.target.value,
                      }))
                    }
                    options={[
                      { label: "Top-up Model (Recommended)", value: "top_up" },
                      { label: "Stack All", value: "sum_all" },
                      { label: "Highest Only", value: "highest_only" },
                    ]}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Top-up pays daily first, then weekly/monthly only pay the extra balance after lower-period bonuses.
                  </p>
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={CalendarDays}
            title="Period Rules"
            description="Enable individual periods and control reset behaviour"
          >
            <div className="space-y-4">
              <Toggle
                label="Enable Daily Incentive"
                checked={!!activeValues.dailyEnabled}
                onChange={(next) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    dailyEnabled: next,
                  }))
                }
              />

              <Field label="Daily Reset Timezone">
                <TextInput
                  value={activeValues.dailyResetTimezone}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      dailyResetTimezone: e.target.value,
                    }))
                  }
                  placeholder="Asia/Kolkata"
                />
              </Field>

              <Toggle
                label="Enable Weekly Incentive"
                checked={!!activeValues.weeklyEnabled}
                onChange={(next) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    weeklyEnabled: next,
                  }))
                }
              />

              <Field label="Week Starts On">
                <SelectInput
                  value={activeValues.weeklyWeekStartsOn}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      weeklyWeekStartsOn: e.target.value,
                    }))
                  }
                  options={[
                    { label: "Monday", value: "MONDAY" },
                    { label: "Sunday", value: "SUNDAY" },
                  ]}
                />
              </Field>

              <Toggle
                label="Enable Monthly Incentive"
                checked={!!activeValues.monthlyEnabled}
                onChange={(next) =>
                  setActiveValues((prev) => ({
                    ...prev,
                    monthlyEnabled: next,
                  }))
                }
              />
            </div>
          </SectionCard>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          {renderSlabEditor(
            "dailySlabs",
            "Daily Slabs",
            "Example: 10 deliveries = ₹100, 15 deliveries = ₹180",
            "Add Daily Slab"
          )}

          {renderSlabEditor(
            "weeklySlabs",
            "Weekly Slabs",
            "Example: 70 deliveries = ₹800",
            "Add Weekly Slab"
          )}

          {renderSlabEditor(
            "monthlySlabs",
            "Monthly Slabs",
            "Example: 300 deliveries = ₹4000",
            "Add Monthly Slab"
          )}
        </div>

        <SectionCard
          icon={Warehouse}
          title="Warehouse Incentive Overrides"
          description="Check which warehouse has custom incentive rules"
        >
          {(visibleOverrides || []).length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-slate-500" style={{ borderColor: BORDER }}>
                    <th className="px-3 py-3">Warehouse</th>
                    <th className="px-3 py-3">State</th>
                    <th className="px-3 py-3">Daily</th>
                    <th className="px-3 py-3">Weekly</th>
                    <th className="px-3 py-3">Monthly</th>
                    <th className="px-3 py-3">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleOverrides.map((item) => (
                    <tr
                      key={item.warehouseId}
                      className="border-b last:border-b-0"
                      style={{ borderColor: BORDER }}
                    >
                      <td className="px-3 py-3 font-semibold text-slate-800">
                        {item.warehouseName || item.warehouseId}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {item.state || "-"}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {summarizeSlabs(item.daily?.slabs)}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {summarizeSlabs(item.weekly?.slabs)}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {summarizeSlabs(item.monthly?.slabs)}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            setActiveValues((prev) => ({
                              ...prev,
                              selectedState: item.state || "",
                              selectedWarehouseId: item.warehouseId,

                              enabled: item.enabled ?? prev.enabled,

                              dailyEnabled: item.daily?.enabled ?? true,
                              dailyResetTimezone:
                                item.daily?.resetTimezone || "Asia/Kolkata",
                              dailySlabs: normalizeIncentiveSlabs(item.daily?.slabs || []),

                              weeklyEnabled: item.weekly?.enabled ?? true,
                              weeklyWeekStartsOn:
                                item.weekly?.weekStartsOn || "MONDAY",
                              weeklySlabs: normalizeIncentiveSlabs(item.weekly?.slabs || []),

                              monthlyEnabled: item.monthly?.enabled ?? true,
                              monthlySlabs: normalizeIncentiveSlabs(item.monthly?.slabs || []),
                            }))
                          }
                          className="rounded-[12px] border px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                          style={{ borderColor: BORDER }}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setActiveValues((prev) => ({
                              ...prev,
                              warehouseOverrides: (prev.warehouseOverrides || []).filter(
                                (x) => String(x.warehouseId) !== String(item.warehouseId)
                              ),
                            }))
                          }
                          className="ml-2 rounded-[12px] border px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50"
                          style={{ borderColor: BORDER }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="rounded-[18px] border border-dashed px-4 py-6 text-center text-sm text-slate-500"
              style={{ borderColor: BORDER }}
            >
              No warehouse incentive override added yet.
            </div>
          )}
        </SectionCard>
      </div>
    );
  };


  const renderUserAuth = () => {
    return (
      <div className="space-y-5">
        <SectionCard
          icon={ShieldCheck}
          title="Customer Login & Signup"
          description="This setting affects only user app/customer auth. Admin panel login stays untouched."
        >
          <div className="space-y-4">
            <Toggle
              label="Allow email signup, email login and email OTP verification"
              checked={!!activeValues.emailAuthEnabled}
              onChange={(next) =>
                setActiveValues((prev) => ({
                  ...prev,
                  emailAuthEnabled: next,
                  phoneAuthEnabled: true,
                }))
              }
            />

            <div
              className="rounded-[16px] border bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800"
              style={{ borderColor: "#bbf7d0" }}
            >
              Phone OTP verification stays enabled always. When email is off, user app hides email fields from login/signup and sends signup OTP to phone. Existing email code is not deleted.
            </div>

            {!activeValues.emailAuthEnabled ? (
              <div
                className="rounded-[16px] border bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800"
                style={{ borderColor: "#fde68a" }}
              >
                Email auth is OFF: customer email login/signup/OTP endpoints will be blocked and the user app will show phone-only auth.
              </div>
            ) : null}
          </div>
        </SectionCard>
      </div>
    );
  };

  const renderReportRetention = () => {
    const updateTablePolicy = (tableKey, patch) => {
      setActiveValues((prev) => ({
        ...prev,
        tables: {
          ...(prev.tables || {}),
          [tableKey]: {
            ...(prev.tables?.[tableKey] || DEFAULT_STATE.reportRetention.tables[tableKey]),
            ...patch,
          },
        },
      }));
    };

    return (
      <div className="space-y-5">
        <SectionCard
          icon={CalendarRange}
          title="Data Archive & Retention"
          description="Only the listed historical audit/log tables are handled. Order, payment, cart, inventory, current driver state and user_last_locations are not touched."
        >
          <div className="space-y-5">
            <Toggle
              label="Enable Automatic Archive & Cleanup"
              checked={!!activeValues.enabled}
              onChange={(next) =>
                setActiveValues((prev) => ({ ...prev, enabled: next }))
              }
            />

            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label="Rows Per Archive Part"
                hint="100 - 50,000"
                info="Each MinIO JSONL.GZ file is created in bounded chunks. Smaller chunks make Admin preview faster; larger chunks reduce object count."
              >
                <TextInput
                  type="number"
                  min={100}
                  max={50000}
                  value={activeValues.batchSize}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      batchSize: parseNumberField(e.target.value),
                    }))
                  }
                />
              </Field>

              <Field
                label="Max Parts Per Table / Run"
                hint="1 - 1,000"
                info="Limits how much one table can archive in a single scheduled run so maintenance cannot create an unbounded database load spike."
              >
                <TextInput
                  type="number"
                  min={1}
                  max={1000}
                  value={activeValues.maxPartsPerTablePerRun}
                  onChange={(e) =>
                    setActiveValues((prev) => ({
                      ...prev,
                      maxPartsPerTablePerRun: parseNumberField(e.target.value),
                    }))
                  }
                />
              </Field>

              <div className="rounded-[16px] border bg-emerald-50 px-4 py-3 md:col-span-2" style={{ borderColor: "#bbf7d0" }}>
                <div className="text-xs font-black uppercase tracking-wide text-emerald-700">Safe purge rule</div>
                <p className="mt-1 text-xs leading-5 text-emerald-800">DB rows are deleted only after MinIO upload, object-size check and SHA-256 read-back verification succeed. If archive storage or verification fails, DB data stays untouched.</p>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={ShieldCheck}
          title="Per-table Retention"
          description="Change these values anytime from Admin Settings. No code or environment change is needed."
        >
          <div className="grid gap-3 xl:grid-cols-2">
            {ARCHIVE_TABLE_FIELDS.map((item) => {
              const policy = activeValues.tables?.[item.key] || DEFAULT_STATE.reportRetention.tables[item.key];
              return (
                <div key={item.key} className="rounded-[16px] border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-black text-slate-700">{item.label}</div>
                      <div className="mt-1 text-xs font-semibold text-slate-400">{item.hint}</div>
                      <div className="mt-1 font-mono text-[11px] text-slate-400">{item.key}</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!policy.enabled}
                      onClick={() => updateTablePolicy(item.key, { enabled: !policy.enabled })}
                      className={`rounded-full border px-3 py-1.5 text-[11px] font-black ${
                        policy.enabled
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-50 text-slate-400"
                      }`}
                    >
                      {policy.enabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                  <div className="mt-3">
                    <Field label="Keep in DB for (days)">
                      <TextInput
                        type="number"
                        min={1}
                        max={3650}
                        disabled={!policy.enabled}
                        value={policy.retentionDays}
                        onChange={(e) =>
                          updateTablePolicy(item.key, { retentionDays: parseNumberField(e.target.value) })
                        }
                      />
                    </Field>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    );
  };

  const renderContent = () => {
    if (activeKey === "userAuth") return renderUserAuth();
    if (activeKey === "delivery") return renderDelivery();
    if (activeKey === "charges") return renderCharges();
    if (activeKey === "scheduler") return renderScheduler();
    if (activeKey === "warehouseDefaults") return renderWarehouseDefaults();
    if (activeKey === "deliveryIncentives") return renderDeliveryIncentives();
    if (activeKey === "reportRetention") return renderReportRetention();
    return null;
  };

  return (
    <div className="flex min-h-0 flex-col gap-6 xl:h-[calc(100vh-150px)] xl:overflow-hidden">
      <StatusPopup
        open={popupState.open}
        type={popupState.type}
        title={popupState.title}
        message={popupState.message}
        buttonText="OK"
        onClose={() =>
          setPopupState((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />

      <div className="flex shrink-0 flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-[30px] font-bold tracking-tight text-[#d86a78]">
            Settings
          </h1>
          {/* <p className="mt-1 text-sm text-slate-500">
            Preset-based configuration management for super admin
          </p> */}
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-[14px] border px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            style={{ borderColor: BORDER }}
          >
            <RefreshCcw size={16} />
            Refresh
          </button>

          <button
            type="button"
            onClick={handleSave}
           disabled={saving || !isDirty}
            className="inline-flex items-center gap-2 rounded-[14px] px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
            style={{ backgroundColor: PINK }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = PINK_HOVER)
            }
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PINK)}
          >
            <Save size={16} />
            {saving ? "Saving..." : isDirty ? "Save Changes" : "No Changes"}
          </button>
        </div>
      </div>

      <div
        className={`grid min-h-0 flex-1 gap-5 xl:overflow-hidden ${
          presetsCollapsed
            ? "xl:grid-cols-[84px_minmax(0,1fr)]"
            : "xl:grid-cols-[300px_minmax(0,1fr)]"
        }`}
      >
        <Card className="h-fit p-3 xl:h-full xl:overflow-hidden">
          <div
            className={`mb-3 flex shrink-0 items-center justify-between ${
              presetsCollapsed ? "xl:justify-center" : ""
            }`}
          >
            <div
              className={`items-center gap-2.5 px-1 ${
                presetsCollapsed ? "flex xl:hidden" : "flex"
              }`}
            >
                <Settings2 size={18} className="text-slate-500" />
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Settings sections</h2>
                  <p className="mt-0.5 text-[11px] text-slate-400">Choose a configuration area</p>
                </div>
              </div>

            <button
              type="button"
              onClick={() => setPresetsCollapsed((prev) => !prev)}
              className="hidden h-9 w-9 shrink-0 place-items-center rounded-[11px] border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 xl:grid"
              title={presetsCollapsed ? "Open settings sidebar" : "Close settings sidebar"}
              aria-label={presetsCollapsed ? "Open settings sidebar" : "Close settings sidebar"}
            >
              {presetsCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>

          <div
            className={`grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:max-h-[calc(100%-48px)] xl:grid-cols-1 xl:overflow-y-auto ${
              presetsCollapsed ? "xl:px-0" : "xl:pr-1"
            }`}
          >
            {PRESETS.map((preset) => {
              const Icon = preset.icon;
              const active = preset.key === activeKey;

              return (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => setActiveKey(preset.key)}
                  title={presetsCollapsed ? preset.title : undefined}
                  className={`group w-full rounded-[14px] border p-3 text-left transition ${
                    presetsCollapsed ? "xl:flex xl:justify-center xl:p-2.5" : ""
                  } ${
                    active
                      ? "border-rose-200 bg-rose-50"
                      : "border-transparent bg-white hover:border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div
                    className={`flex items-center gap-3 ${
                      presetsCollapsed ? "xl:justify-center xl:gap-0" : ""
                    }`}
                  >
                    <div
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-[12px] transition ${
                        active
                          ? "bg-white text-rose-600 shadow-sm"
                          : "bg-slate-100 text-slate-500 group-hover:bg-white"
                      }`}
                    >
                      <Icon size={18} />
                    </div>

                    <div className={`min-w-0 ${presetsCollapsed ? "xl:hidden" : ""}`}>
                        <div className={`text-[13px] font-bold ${active ? "text-rose-700" : "text-slate-800"}`}>
                          {preset.title}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-slate-400">
                          {preset.description}
                        </p>
                      </div>
                    </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="min-h-0 p-4 sm:p-6 xl:flex xl:flex-col xl:overflow-hidden">
          <div className="mb-6 flex shrink-0 flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3.5">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-rose-50 text-rose-600">
                {React.createElement(activePreset.icon, { size: 20 })}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-[22px] font-bold text-slate-900">
                  {activePreset.title}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <p className="text-sm text-slate-500">{activePreset.description}</p>
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-500">
                    {activePreset.key}
                  </span>
                </div>
              </div>
            </div>

            <span
              className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                loading
                  ? "bg-amber-50 text-amber-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${loading ? "bg-amber-500" : "bg-emerald-500"}`} />
              {loading ? "Loading" : "Ready to edit"}
            </span>
          </div>

          <div className="xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pr-2">
            {loading ? (
              <div className="rounded-[18px] border border-dashed border-slate-200 bg-slate-50/50 p-12 text-center text-sm text-slate-500">
                Loading settings...
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
